import { defineStore } from 'pinia'
import axios from 'axios'

import { getApiBaseUrl } from '../utils/apiBaseUrl'

const API_BASE_URL = getApiBaseUrl()

// Создаем axios instance с базовой конфигурацией
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 секунд timeout для всех запросов
})

// Добавляем interceptor для автоматического добавления токена
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    // Проверяем, не истек ли токен
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const now = Date.now() / 1000
      if (payload.exp && payload.exp < now) {
        console.warn('Token expired, removing from storage')
        localStorage.removeItem('token')
        window.location.href = '/login'
        return Promise.reject(new Error('Token expired'))
      }
      config.headers.Authorization = `Bearer ${token}`
    } catch (e) {
      console.warn('Invalid token format, removing from storage')
      localStorage.removeItem('token')
      window.location.href = '/login'
      return Promise.reject(new Error('Invalid token'))
    }
  }
  return config
})

// Retry logic for network errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Do not retry / redirect on aborted (canceled) requests.
    // Axios v1 uses CanceledError with code 'ERR_CANCELED'.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error)
    }

    const config = error.config
    
    // Allow per-request opt-out (important for long-running endpoints like reanalyze)
    if (config?.__noRetry === true) {
      // Проверяем auth errors
      const status = error?.response?.status
      if (status === 401) {
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    // Retry on network/timeout errors (но не на auth errors)
    if (!config || config.__retryCount >= 2) {
      // Проверяем auth errors
      const status = error?.response?.status
      if (status === 401) {
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
    
    // Retry только для network/timeout errors
    const shouldRetry = 
      error.code === 'ECONNABORTED' || // timeout
      error.code === 'ERR_NETWORK' ||  // network error
      !error.response                   // no response (connection lost)
    
    if (shouldRetry) {
      config.__retryCount = config.__retryCount || 0
      config.__retryCount += 1
      
      console.warn(`[AXIOS] Retry ${config.__retryCount}/2 for ${config.url}`)
      
      // Exponential backoff: 1s, 2s
      const delay = config.__retryCount * 1000
      await new Promise(resolve => setTimeout(resolve, delay))
      
      return api.request(config)
    }
    
    // Проверяем auth errors для остальных случаев
    const status = error?.response?.status
    if (status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

export const useApiStore = defineStore('api', {
  state: () => ({
    loading: false,
    error: null as string | null,
  }),

  actions: {
    async getChatActions(chatId: number, limit: number = 50) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/tasks/chat/${Number(chatId)}/actions`, {
          params: { limit: Number(limit || 50) },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки действий по чату'
        throw error
      } finally {
        this.loading = false
      }
    },
    // Аутентификация
    async login(username: string, password: string) {
      this.loading = true
      this.error = null
      try {
        // API ожидает FormData
        const formData = new FormData()
        formData.append('username', username)
        formData.append('password', password)
        
        const response = await api.post('/api/v1/auth/login', formData)
        
        localStorage.setItem('token', response.data.access_token)
        return response.data
      } catch (error: any) {
        this.error = error.response?.data?.detail || 'Ошибка входа'
        throw error
      } finally {
        this.loading = false
      }
    },

    async register(userData: any) {
      this.loading = true
      this.error = null
      try {
        const response = await api.post('/api/v1/auth/register', userData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        return response.data
      } catch (error: any) {
        this.error = error.response?.data?.detail || 'Ошибка регистрации'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getCurrentUser() {
      try {
        const token = localStorage.getItem('token')
        const response = await api.get(`/api/v1/auth/me?token=${token}`)
        return response.data
      } catch (error: any) {
        this.error = error.response?.data?.detail || 'Ошибка получения данных пользователя'
        throw error
      }
    },

    /** Дашборд P&L для акционеров: только тенанты из SHAREHOLDERS_REPORT_TENANT_IDS на бэкенде. */
    async getShareholdersAccess(): Promise<{ allowed: boolean; tenant_id: number | null }> {
      try {
        const response = await api.get('/api/v1/shareholders-report/access')
        return response.data
      } catch {
        return { allowed: false, tenant_id: null }
      }
    },

    async getShareholdersPnlTotal(params: { start_date: string; end_date: string }) {
      const response = await api.get('/api/v1/shareholders-report/pnl-total', {
        params,
        timeout: 120000,
      })
      return response.data
    },

    async getShareholdersPivotData(params: { start_date: string; end_date: string }) {
      const response = await api.get('/api/v1/shareholders-report/pivot-data', {
        params,
        timeout: 120000,
      })
      return response.data
    },

    async getShareholdersPivotDataV2(params: { start_date: string; end_date: string; group_fields: string }) {
      const response = await api.get('/api/v1/shareholders-report/pivot-data/v2', {
        params,
        timeout: 60000,
      })
      return response.data
    },

    async clearShareholdersCache(): Promise<{ cleared: number }> {
      const response = await api.post('/api/v1/shareholders-report/cache/clear')
      return response.data
    },

    async getCommercialPnlAccess(): Promise<{ allowed: boolean; tenant_id: number | null }> {
      try {
        const response = await api.get('/api/v1/commercial-pnl/access')
        return response.data
      } catch {
        return { allowed: false, tenant_id: null }
      }
    },
    async getCommercialPnlSummary(params: { start_date: string; end_date: string }) {
      const response = await api.get('/api/v1/commercial-pnl/summary', { params })
      return response.data
    },

    async getCommercialPnlMatrix(params: { mode: string; start_date: string; end_date: string }) {
      const response = await api.get('/api/v1/commercial-pnl/matrix', { params })
      return response.data
    },

    async getCommercialPnlDrill(params: Record<string, any>) {
      const response = await api.get('/api/v1/commercial-pnl/drill', {
        params,
        timeout: 120000,
      })
      return response.data
    },

    async getShareholdersDrill(params: Record<string, any>) {
      const response = await api.get('/api/v1/shareholders-report/drill', {
        params,
        timeout: 120000,
      })
      return response.data
    },

    async getMnClientInvoicesSummary(params?: {
      month_from?: string
      month_to?: string
      industry?: string
      q?: string
    }) {
      const response = await api.get('/api/v1/mn-client-invoices/summary', { params })
      return response.data
    },

    async listMnClientInvoiceClients(params?: {
      month_from?: string
      month_to?: string
      industry?: string
      q?: string
      page?: number
      page_size?: number
    }) {
      const response = await api.get('/api/v1/mn-client-invoices/clients', { params })
      return response.data
    },

    async getMnClientInvoiceIndustrySeries(params?: {
      month_from?: string
      month_to?: string
      industry?: string
      q?: string
    }) {
      const response = await api.get('/api/v1/mn-client-invoices/industry-series', { params })
      return response.data
    },

    async getMnClientInvoiceClientSeries(params?: {
      month_from?: string
      month_to?: string
      industry?: string
      q?: string
    }) {
      const response = await api.get('/api/v1/mn-client-invoices/client-series', { params })
      return response.data
    },

    async getMnClientInvoiceIndustries() {
      const response = await api.get('/api/v1/mn-client-invoices/industries')
      return response.data
    },

    async getMnClientInvoiceDetail(
      companyUniqueId: number,
      params?: { month_from?: string; month_to?: string; tenant_id?: number },
    ) {
      const response = await api.get(`/api/v1/mn-client-invoices/clients/${companyUniqueId}`, { params })
      return response.data
    },

    // LLM logs (requests/usage)
    async getLlmLogs(params?: {
      page?: number,
      page_size?: number,
      since?: string,
      until?: string,
      provider?: string,
      model?: string,
      source?: string,
      ok?: number, // 0|1
      chat_id?: number,
      include_raw?: number, // 0|1
    }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm/logs', { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM логов'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmLogById(logId: number) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm/logs/${logId}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM лога'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmLogsByMessage(params: {
      chat_id: number,
      telegram_message_id: number,
      limit?: number,
      include_raw?: number, // 0|1
    }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm/logs/by-message', {
          params: {
            chat_id: params.chat_id,
            telegram_message_id: params.telegram_message_id,
            limit: params.limit ?? 50,
            include_raw: params.include_raw ?? 1,
          },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM логов по сообщению'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmLiveStatus(params: {
      chat_id: number
      lookback_seconds?: number
    }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm/live-status', {
          params: {
            chat_id: params.chat_id,
            lookback_seconds: params.lookback_seconds ?? 900,
          },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки live LLM статуса'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmLogsHourlyStats(params?: {
      since?: string,
      until?: string,
      provider?: string,
      model?: string,
      source?: string,
      ok?: number, // 0|1
    }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm/logs/stats/hourly', { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки статистики LLM'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Materialize blocked items (manual control)
    async getMaterializeBlocked(params?: { limit?: number }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/materialize/blocked', { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки blocked materialize'
        throw error
      } finally {
        this.loading = false
      }
    },

    async requeueMaterializeBlocked(analyticsItemId: number, fixedErrorCode?: number) {
      this.loading = true
      try {
        const response = await api.post(`/api/v1/materialize/blocked/${analyticsItemId}/requeue`, {
          fixed_error_code: fixedErrorCode ?? null,
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка возврата materialize в очередь'
        throw error
      } finally {
        this.loading = false
      }
    },

    // LLM billing block (OpenRouter)
    async getLlmBillingStatus(provider: string = 'openrouter') {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm/billing/status', { params: { provider } })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM billing status'
        throw error
      } finally {
        this.loading = false
      }
    },

    async unblockLlmBilling(provider: string = 'openrouter') {
      this.loading = true
      try {
        const response = await api.post('/api/v1/llm/billing/unblock', { provider })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка разблокировки LLM'
        throw error
      } finally {
        this.loading = false
      }
    },

    // OpenRouter balance / credits (best-effort)
    async getOpenrouterBalance() {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm/billing/openrouter/balance')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки OpenRouter balance'
        throw error
      } finally {
        this.loading = false
      }
    },

    // LLM Evals (QA system)
    async listLlmEvalRuns(params?: { limit?: number, offset?: number }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm-evals/runs', { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval runs'
        throw error
      } finally {
        this.loading = false
      }
    },

    async listLlmEvalSuites(params?: { limit?: number }) {
      this.loading = true
      try {
        const response = await api.get('/api/v1/llm-evals/suites', { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval suites'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRun(runId: number) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval run'
        throw error
      } finally {
        this.loading = false
      }
    },

    async createLlmEvalRun(payload: { suite_id: number, params?: Record<string, any> }) {
      this.loading = true
      try {
        const response = await api.post('/api/v1/llm-evals/runs', payload, {
          headers: { 'Content-Type': 'application/json' },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка создания LLM eval run'
        throw error
      } finally {
        this.loading = false
      }
    },

    async cloneLlmEvalRunToId(runId: number, payload: { source_run_id: number, params_mode?: 'source' | 'merge' | 'replace', params?: Record<string, any> }) {
      this.loading = true
      try {
        const response = await api.post(`/api/v1/llm-evals/runs/${runId}/clone`, payload, {
          headers: { 'Content-Type': 'application/json' },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка создания LLM eval run (clone)'
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateLlmEvalRunParams(runId: number, payload: { mode?: 'replace' | 'merge', params: Record<string, any> }) {
      this.loading = true
      try {
        const response = await api.post(`/api/v1/llm-evals/runs/${runId}/params`, payload, {
          headers: { 'Content-Type': 'application/json' },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка сохранения параметров LLM eval run'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRunSteps(runId: number) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}/steps`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval steps'
        throw error
      } finally {
        this.loading = false
      }
    },

    async enqueueLlmEvalStep(runId: number, stepKey: string) {
      this.loading = true
      try {
        const response = await api.post(`/api/v1/llm-evals/runs/${runId}/steps/${encodeURIComponent(stepKey)}/enqueue`, {})
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка запуска шага'
        throw error
      } finally {
        this.loading = false
      }
    },

    async resetLlmEvalStep(runId: number, stepKey: string, payload: { clear_data: boolean, reset_downstream: boolean }) {
      this.loading = true
      try {
        const response = await api.post(`/api/v1/llm-evals/runs/${runId}/steps/${encodeURIComponent(stepKey)}/reset`, payload, {
          headers: { 'Content-Type': 'application/json' },
        })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка перезапуска шага'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalSuiteQuestions(suiteId: number) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/suites/${suiteId}/questions`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval questions'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRunItems(runId: number, params?: { include_raw?: number }) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}/items`, { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval items'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRunJudgements(runId: number, params?: { include_raw?: number }) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}/judgements`, { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval judgements'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRunMissing(runId: number, params?: { include_raw?: number, limit?: number }) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}/missing`, { params })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval missing'
        throw error
      } finally {
        this.loading = false
      }
    },

    async backfillLlmEvalRunMissing(runId: number) {
      this.loading = true
      try {
        const response = await api.post(`/api/v1/llm-evals/runs/${runId}/backfill-missing`, {})
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка дозаполнения пропусков'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRunRankingByQuestion(runId: number) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}/ranking-by-question`, { timeout: 120000, __noRetry: true } as any)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval ranking-by-question'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalRunQaCompare(runId: number, params?: { baseline_model_contains?: string }) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/runs/${runId}/qa-compare`, { params, timeout: 120000, __noRetry: true } as any)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки LLM eval qa-compare'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLlmEvalPromptVersion(promptVersionId: number) {
      this.loading = true
      try {
        const response = await api.get(`/api/v1/llm-evals/prompt-versions/${promptVersionId}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки prompt version'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Заметки
    async getNotes() {
      this.loading = true
      try {
        const response = await api.get('/api/v1/notes')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки заметок'
        throw error
      } finally {
        this.loading = false
      }
    },

    async createNote(noteData: any) {
      this.loading = true
      try {
        const response = await api.post('/api/v1/notes', noteData)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка создания заметки'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Теги
    async getTags() {
      this.loading = true
      try {
        const response = await api.get('/api/v1/tags')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки тегов'
        throw error
      } finally {
        this.loading = false
      }
    },

    async createTag(tagData: any) {
      this.loading = true
      try {
        const response = await api.post('/api/v1/tags', tagData)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка создания тега'
        throw error
      } finally {
        this.loading = false
      }
    },

    // LLM функционал
    async searchLLM(data: { content: string, chat_ids?: number[], session_id?: string, history?: { role: 'user'|'assistant', content: string }[] }) {
      this.loading = true
      try {
        const response = await api.post('/api/v1/llm/search', data)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка поиска в базе знаний'
        throw error
      } finally {
        this.loading = false
      }
    },

    async listLLMConversations() {
      try {
        const response = await api.get('/api/v1/llm/conversations')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки истории диалогов'
        throw error
      }
    },

    async getLLMConversation(sessionId: string) {
      try {
        const response = await api.get(`/api/v1/llm/conversations/${sessionId}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки диалога'
        throw error
      }
    },

    async deleteLLMConversation(sessionId: string) {
      try {
        const response = await api.delete(`/api/v1/llm/conversations/${sessionId}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка удаления диалога'
        throw error
      }
    },

    async processLLM(data: { content: string }) {
      this.loading = true
      try {
        const response = await api.post('/api/v1/llm/process', data)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка обработки информации'
        throw error
      } finally {
        this.loading = false
      }
    },

    async autoTagLLM(data: { content: string }) {
      this.loading = true
      try {
        const response = await api.post('/api/v1/llm/tag', data)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка автоматического тегирования'
        throw error
      } finally {
        this.loading = false
      }
    },

    async getLLMStatus() {
      try {
        const response = await api.get('/api/v1/llm/status')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка получения статуса LLM'
        throw error
      }
    },

    // Пользовательские настройки
    async getUserSettings() {
      try {
        const response = await api.get('/api/v1/user/settings')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка получения настроек пользователя'
        throw error
      }
    },

    async updateUserSettings(settings: any) {
      try {
        const response = await api.post('/api/v1/user/settings', settings)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка обновления настроек пользователя'
        throw error
      }
    },

    async testUserSettings() {
      try {
        const response = await api.post('/api/v1/user/settings/test')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка тестирования настроек'
        throw error
      }
    },

    // Статистика
    async getStats() {
      try {
        const response = await api.get('/api/v1/stats')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки статистики'
        throw error
      }
    },

    // Telegram интеграция
    async getTelegramBotStatus() {
      try {
        const response = await api.get('/api/v1/telegram/bot/status')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка получения статуса бота'
        throw error
      }
    },

    async startTelegramBot() {
      try {
        const response = await api.post('/api/v1/telegram/bot/start')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка запуска бота'
        throw error
      }
    },

    async stopTelegramBot() {
      try {
        const response = await api.post('/api/v1/telegram/bot/stop')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка остановки бота'
        throw error
      }
    },

    async getTelegramStats() {
      try {
        const response = await api.get('/api/v1/telegram/stats')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки статистики Telegram'
        throw error
      }
    },

    async getTelegramChats() {
      try {
        const response = await api.get('/api/v1/telegram/chats')
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки чатов'
        throw error
      }
    },

    async searchChats(q?: string, types?: string, page: number = 1, page_size: number = 20) {
      try {
        const params = new URLSearchParams()
        if (q) params.set('q', q)
        if (types) params.set('types', types)
        params.set('page', String(page))
        params.set('page_size', String(page_size))
        const response = await api.get(`/api/v1/telegram/chats/search?${params.toString()}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка поиска чатов'
        throw error
      }
    },

    async getChatTopics(chat_id: number, page: number = 1, page_size: number = 20) {
      try {
        const response = await api.get(`/api/v1/telegram/chats/${chat_id}/topics?page=${page}&page_size=${page_size}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки топиков'
        throw error
      }
    },

    async checkSync(chat_telegram_id: number, topic_id?: number) {
      try {
        const url = topic_id != null
          ? `/api/v1/telethon/check_sync?chat_telegram_id=${chat_telegram_id}&topic_id=${topic_id}`
          : `/api/v1/telethon/check_sync?chat_telegram_id=${chat_telegram_id}`
        const response = await api.get(url)
        return response.data as { remote_total: number, local_total: number, in_sync: boolean }
      } catch (error: any) {
        this.error = 'Ошибка проверки синхронизации'
        throw error
      }
    },

    async smartReparse(
      useGetMessages: boolean = true, 
      reparseAll: boolean = false, 
      updateMissingCounts: boolean = false,
      telethonAccountId?: number | null,
      limit?: number | null
    ) {
      try {
        const params: any = {
          use_get_messages: useGetMessages,
          reparse_all: reparseAll,
          update_missing_counts: updateMissingCounts
        }
        if (telethonAccountId) params.telethon_account_id = telethonAccountId
        if (limit) params.limit = limit
        
        const response = await api.post('/api/v1/telethon/smart_reparse', null, { params })
        return response.data as {
          success: boolean,
          processed_count: number,
          pinned_count: number,
          stopped_at_chat: string | null,
          total_dialogs: number,
          api_calls: Record<string, number>,
          sync_id: number,
          duration_seconds: number
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка умного обновления чатов'
        throw error
      }
    },

    async getSyncHistory(limit: number = 10) {
      try {
        const response = await api.get('/api/v1/telethon/sync_history', {
          params: { limit }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки истории синхронизаций'
        throw error
      }
    },

    async getSyncStats() {
      try {
        const response = await api.get('/api/v1/telethon/sync_stats')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки статистики синхронизаций'
        throw error
      }
    },

    async getTelethonCatchupStatus(tag: string = 'active_sending') {
      try {
        const response = await api.get('/api/v1/telethon/catchup/status', {
          params: { tag }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки статуса догонки Telegram'
        throw error
      }
    },

    async triggerTelethonCatchup(tag: string = 'active_sending', reason: string = 'manual') {
      try {
        const response = await api.post('/api/v1/telethon/catchup/trigger', { reason }, {
          params: { tag }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска догонки Telegram'
        throw error
      }
    },

    async bootstrapTelethonAccountChats(telethon_account_id: number, bootstrap_limit: number = 500, reason: string = 'manual_ui') {
      try {
        const response = await api.post('/api/v1/telethon/catchup/bootstrap_all', {
          reason,
          bootstrap_limit
        }, {
          params: { telethon_account_id }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска bootstrap (500 сообщений) для учетки'
        throw error
      }
    },

    async getTelethonCatchupConfig(tag: string = 'active_sending') {
      try {
        const response = await api.get('/api/v1/telethon/catchup/config', {
          params: { tag }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки настроек догонки Telegram'
        throw error
      }
    },

    async updateTelethonCatchupConfig(tag: string = 'active_sending', patch: any) {
      try {
        const response = await api.put('/api/v1/telethon/catchup/config', patch, {
          params: { tag }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка сохранения настроек догонки Telegram'
        throw error
      }
    },

    async getTelethonCatchupJobs(tag: string = 'active_sending', limit: number = 50) {
      try {
        const response = await api.get('/api/v1/telethon/catchup/jobs', {
          params: { tag, limit }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки прогресса догонки Telegram'
        throw error
      }
    },

    async recountMessagesOnly(telethonAccountId?: number | null) {
      try {
        const params: any = {}
        if (telethonAccountId) params.telethon_account_id = telethonAccountId
        const response = await api.post('/api/v1/telethon/recount_messages_only', null, { params })
        return response.data as {
          processed: number,
          total: number,
          errors: number
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка перезагрузки счетчиков'
        throw error
      }
    },

    async deleteAllChats() {
      try {
        const response = await api.delete('/api/v1/telethon/delete_all_chats')
        return response.data as {
          deleted: number,
          message: string
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления чатов'
        throw error
      }
    },

    async leaveChannel(chatId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/leave_channel/${chatId}`)
        return response.data as {
          success: boolean,
          message: string
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка выхода из канала'
        throw error
      }
    },

    async unifiedChats(
      q?: string,
      types?: string,
      page: number = 1,
      page_size: number = 20,
      telethon_account_id?: number | null,
      telethon_account_tag?: string | null,
      sort?: string | null,
      llm_stage?: string[] | null,
      llm_interest_min?: number | null,
      llm_interest_max?: number | null,
      llm_stage_num_min?: number | null,
      llm_stage_num_max?: number | null,
      llm_biz_size_min?: number | null,
      llm_biz_size_max?: number | null,
      planned_only?: number | null,
      include_ssot?: number | null,
      opts?: { signal?: AbortSignal, include_total?: number }
    ) {
      try {
        const params = new URLSearchParams()
        if (q) params.set('q', q)
        if (types) params.set('types', types)
        if (telethon_account_id) params.set('telethon_account_id', String(telethon_account_id))
        if (telethon_account_tag) params.set('telethon_account_tag', String(telethon_account_tag))
        if (sort) params.set('sort', String(sort))
        if (llm_stage && Array.isArray(llm_stage)) {
          for (const st of llm_stage) {
            const s = String(st || '').trim()
            if (s) params.append('llm_stage', s)
          }
        }
        if (llm_interest_min != null) params.set('llm_interest_min', String(llm_interest_min))
        if (llm_interest_max != null) params.set('llm_interest_max', String(llm_interest_max))
        if (llm_stage_num_min != null) params.set('llm_stage_num_min', String(llm_stage_num_min))
        if (llm_stage_num_max != null) params.set('llm_stage_num_max', String(llm_stage_num_max))
        if (llm_biz_size_min != null) params.set('llm_biz_size_min', String(llm_biz_size_min))
        if (llm_biz_size_max != null) params.set('llm_biz_size_max', String(llm_biz_size_max))
        if (planned_only != null && Number(planned_only) === 1) params.set('planned_only', '1')
        if (include_ssot != null && Number(include_ssot) === 1) params.set('include_ssot', '1')
        if (opts?.include_total != null && Number(opts.include_total) === 0) params.set('include_total', '0')
        params.set('page', String(page))
        params.set('page_size', String(page_size))
        const response = await api.get(`/api/v1/telegram/chats/unified?${params.toString()}`, { signal: opts?.signal } as any)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки чатов/топиков'
        throw error
      }
    },

    async setChatAutoReply(
      chatId: number,
      enabledOrMode: boolean | 'inherit' | 'on' | 'off',
      ai_bot_id?: number | null
    ) {
      try {
        const payload: any = {}
        if (typeof enabledOrMode === 'string') {
          payload.mode = enabledOrMode
        } else {
          payload.enabled = !!enabledOrMode
        }
        if (ai_bot_id !== undefined) payload.ai_bot_id = ai_bot_id
        const response = await api.post(`/api/v1/telegram/chats/${chatId}/auto-reply`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления автоответа'
        throw error
      }
    },

    async setChatDialogSubjectOverride(chatId: number, value: 'our_outreach' | 'not_outreach' | null) {
      try {
        const payload: any = { value: value || null }
        const response = await api.post(`/api/v1/telegram/chats/${chatId}/dialog-subject-override`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления subject override'
        throw error
      }
    },

    async getTelegramChatGrants(chatId: number) {
      const response = await api.get(`/api/v1/telegram/chats/${chatId}/grants`)
      return response.data as { grants: Array<{ user_id: number, username: string, email: string, permission: string, expires_at?: string | null }> }
    },

    async upsertTelegramChatGrant(
      chatId: number,
      payload: { user_id: number, permission: 'read' | 'send' | 'write' | 'admin' }
    ) {
      const response = await api.put(`/api/v1/telegram/chats/${chatId}/grants`, payload)
      return response.data
    },

    async deleteTelegramChatGrant(chatId: number, userId: number) {
      const response = await api.delete(`/api/v1/telegram/chats/${chatId}/grants/${userId}`)
      return response.data
    },

    // Telethon per-user settings
    // ==========================================
    // Multiple Telethon Accounts Management
    // ==========================================
    
    async getTelethonAccounts() {
      try {
        const response = await api.get('/api/v1/telethon/accounts')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки списка Telethon учеток'
        throw error
      }
    },

    async getTelethonAccountTags() {
      try {
        const response = await api.get('/api/v1/telethon/account-tags')
        return response.data as { tags: string[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки тегов Telethon учеток'
        throw error
      }
    },

    async updateTelethonAccountTags(accountId: number, tags: string[]) {
      try {
        const response = await api.put(`/api/v1/telethon/accounts/${accountId}/tags`, { tags })
        return response.data as { message: string, tags: string[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления тегов Telethon учетки'
        throw error
      }
    },

    async getTelethonAccountGrants(accountId: number) {
      const response = await api.get(`/api/v1/telethon/accounts/${accountId}/grants`)
      return response.data as { grants: Array<{ user_id: number, username: string, email: string, permission: string, expires_at?: string | null }> }
    },

    async upsertTelethonAccountGrant(
      accountId: number,
      payload: { user_id: number, permission: 'read' | 'send' | 'write' | 'admin' }
    ) {
      const response = await api.put(`/api/v1/telethon/accounts/${accountId}/grants`, payload)
      return response.data
    },

    async deleteTelethonAccountGrant(accountId: number, userId: number) {
      const response = await api.delete(`/api/v1/telethon/accounts/${accountId}/grants/${userId}`)
      return response.data
    },

    // Analytics endpoints
    async getResponseRateAnalytics(tag: string = 'active_sending') {
      const response = await api.get('/api/v1/telethon/analytics/response-rate', {
        params: { tag }
      })
      return response.data
    },

    async getConversationDepthAnalytics(tag: string = 'active_sending') {
      const response = await api.get('/api/v1/telethon/analytics/conversation-depth', {
        params: { tag }
      })
      return response.data
    },

    async getReplySpeedAnalytics(tag: string = 'active_sending') {
      const response = await api.get('/api/v1/telethon/analytics/reply-speed', {
        params: { tag }
      })
      return response.data
    },

    async getReplySpeedTimeline(tag: string = 'active_sending', days: number = 30) {
      const response = await api.get('/api/v1/telethon/analytics/reply-speed-timeline', {
        params: { tag, days }
      })
      return response.data
    },

    async getTelethonAccount(accountId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки Telethon учетки'
        throw error
      }
    },

    async createTelethonAccount(payload: { name: string, api_id: number, api_hash: string, phone?: string }) {
      try {
        const response = await api.post('/api/v1/telethon/accounts', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания Telethon учетки'
        throw error
      }
    },

    async updateTelethonAccount(accountId: number, payload: { 
      name?: string, 
      api_id?: number, 
      api_hash?: string, 
      phone?: string, 
      is_active?: boolean,
      auto_reply_enabled?: boolean,
      default_ai_bot_id?: number | null,
      ai_staff_member_id?: number | null
    }) {
      try {
        const response = await api.put(`/api/v1/telethon/accounts/${accountId}`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления Telethon учетки'
        throw error
      }
    },

    async setDefaultTelethonAccount(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/set-default`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка установки учетки по умолчанию'
        throw error
      }
    },

    async deleteTelethonAccount(accountId: number) {
      try {
        const response = await api.delete(`/api/v1/telethon/accounts/${accountId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления Telethon учетки'
        throw error
      }
    },

    // ==========================================
    // AI Staff + Email Accounts (MVP)
    // ==========================================

    async getEmailAccounts() {
      try {
        const response = await api.get('/api/v1/email-accounts')
        return response.data as { items: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки почтовых аккаунтов'
        throw error
      }
    },

    async createEmailAccount(payload: any) {
      try {
        const response = await api.post('/api/v1/email-accounts', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания почтового аккаунта'
        throw error
      }
    },

    async updateEmailAccount(emailAccountId: number, payload: any) {
      try {
        const response = await api.put(`/api/v1/email-accounts/${emailAccountId}`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления почтового аккаунта'
        throw error
      }
    },

    async deleteEmailAccount(emailAccountId: number) {
      try {
        const response = await api.delete(`/api/v1/email-accounts/${emailAccountId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления почтового аккаунта'
        throw error
      }
    },

    async testSendEmail(emailAccountId: number, payload: { to_email: string, subject?: string, body?: string }) {
      try {
        const response = await api.post(`/api/v1/email-accounts/${emailAccountId}/test-send`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка тестовой отправки email'
        throw error
      }
    },

    async getAiStaff() {
      try {
        const response = await api.get('/api/v1/ai-staff')
        return response.data as { items: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки AI-сотрудников'
        throw error
      }
    },

    async createAiStaff(payload: any) {
      try {
        const response = await api.post('/api/v1/ai-staff', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания AI-сотрудника'
        throw error
      }
    },

    async updateAiStaff(staffId: number, payload: any) {
      try {
        const response = await api.put(`/api/v1/ai-staff/${staffId}`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления AI-сотрудника'
        throw error
      }
    },

    async deleteAiStaff(staffId: number) {
      try {
        const response = await api.delete(`/api/v1/ai-staff/${staffId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления AI-сотрудника'
        throw error
      }
    },

    async getTelethonAccountProfile(accountId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}/profile`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки профиля'
        throw error
      }
    },

    async updateTelethonAccountProfile(accountId: number, payload: { first_name?: string, last_name?: string, about?: string }) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/profile`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления профиля'
        throw error
      }
    },

    async updateTelethonAccountUsername(accountId: number, username?: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/username`, { username })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления username'
        throw error
      }
    },

    async updateTelethonAccountPhoto(accountId: number, formData: FormData) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки фото'
        throw error
      }
    },

    async updateTelethonAccountColor(accountId: number, color: string) {
      try {
        const response = await api.patch(`/api/v1/telethon/accounts/${accountId}/color?color=${encodeURIComponent(color)}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления цвета'
        throw error
      }
    },

    // Two-Factor Authentication (2FA)
    async get2FAStatus(accountId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}/2fa/status`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка получения статуса 2FA'
        throw error
      }
    },

    async check2FATelegram(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/2fa/check`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка проверки 2FA в Telegram'
        throw error
      }
    },

    async enable2FA(accountId: number, password: string, hint?: string, email?: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/2fa/enable`, {
          password,
          hint,
          email
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка включения 2FA'
        throw error
      }
    },

    async disable2FA(accountId: number, currentPassword: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/2fa/disable`, {
          current_password: currentPassword
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка выключения 2FA'
        throw error
      }
    },

    async confirm2FAEmail(accountId: number, code: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/2fa/email/confirm`, {
          code
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка подтверждения email для 2FA'
        throw error
      }
    },

    // Telegram Profile Management
    async getTelegramProfile(accountId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}/profile`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка получения профиля Telegram'
        throw error
      }
    },

    async updateTelegramProfile(accountId: number, payload: { first_name?: string, last_name?: string, about?: string }) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/profile`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления профиля Telegram'
        throw error
      }
    },

    async uploadTelegramProfilePhoto(accountId: number, photoFile: File) {
      try {
        const formData = new FormData()
        formData.append('photo', photoFile)
        
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/profile/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки фотографии профиля'
        throw error
      }
    },

    async getAllTelegramProfilePhotos(accountId: number, limit: number = 20) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}/profile/photos`, {
          params: { limit }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка получения фотографий профиля'
        throw error
      }
    },

    async deleteTelegramProfilePhoto(accountId: number) {
      try {
        const response = await api.delete(`/api/v1/telethon/accounts/${accountId}/profile/photo`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления фотографии профиля'
        throw error
      }
    },

    async deleteSpecificTelegramProfilePhoto(accountId: number, photoId: string) {
      try {
        // Передаем ID как строку чтобы избежать потери точности больших чисел
        const response = await api.delete(`/api/v1/telethon/accounts/${accountId}/profile/photo/${photoId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления фотографии'
        throw error
      }
    },

    async updateTelegramUsername(accountId: number, username: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/profile/username`, null, {
          params: { username }
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления username'
        throw error
      }
    },

    // ==========================================
    // Перезахват купленной учётки и персоны (№5348)
    // ==========================================

    // Список сеансов учётки (account.GetAuthorizations через живой клиент).
    async telethonAccountSessions(accountId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}/sessions`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка получения сеансов'
        throw error
      }
    },

    // Завершить все другие сеансы (auth.ResetAuthorizations), кроме текущего.
    async telethonResetOtherSessions(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/sessions/reset-others`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка завершения других сеансов'
        throw error
      }
    },

    // Перевыпустить наш сеанс: новый ключ тем же прокси, рантайм на него,
    // старый ключ погасить. Код входа читает живой клиент из чата 777000.
    async telethonReissueSession(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/sessions/reissue`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка перевыпуска сеанса'
        throw error
      }
    },

    // Массовое применение персон по списку учёток, с паузами между ними.
    async telethonPersonasApply(
      items: Array<{ account_id: number, first_name?: string, last_name?: string, about?: string, username?: string, avatar_rezhim?: string, avatar_put?: string }>,
      pauzaSek: number = 30,
    ) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/personas/apply`, {
          items,
          pauza_sek: pauzaSek,
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка применения персон'
        throw error
      }
    },

    // ==========================================
    // NEW: Multi-account authorization endpoints
    // ==========================================

    async telethonAccountSendCode(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/send_code`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка отправки кода'
        throw error
      }
    },

    async telethonAccountConfirm(accountId: number, payload: { code: string, password?: string }) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/confirm`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка подтверждения'
        throw error
      }
    },

    async telethonAccountLogout(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/logout`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка выхода'
        throw error
      }
    },

    async telethonAccountQrStart(accountId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/qr/start`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска QR авторизации'
        throw error
      }
    },

    async telethonAccountQrStatus(accountId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/accounts/${accountId}/qr/status`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка проверки статуса QR'
        throw error
      }
    },

    async telethonAccountQrConfirmPassword(accountId: number, password: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${accountId}/qr/confirm_password`, { password })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка подтверждения пароля'
        throw error
      }
    },

    // ==========================================
    // Legacy methods for backward compatibility
    // ==========================================

    async getTelethonSettings() {
      try {
        const response = await api.get('/api/v1/telethon/settings')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки Telethon настроек'
        throw error
      }
    },
    async updateTelethonSettings(payload: { api_id?: number, api_hash?: string, phone?: string }) {
      try {
        const response = await api.post('/api/v1/telethon/settings', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка сохранения Telethon настроек'
        throw error
      }
    },
    async telethonSendCode() {
      try {
        const response = await api.post('/api/v1/telethon/send_code')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка отправки кода'
        throw error
      }
    },
    async telethonConfirm(payload: { code: string, password?: string }) {
      try {
        const response = await api.post('/api/v1/telethon/confirm', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка подтверждения'
        throw error
      }
    },
    async telethonBackfill(payload: { limit?: number, dialogs?: string }) {
      try {
        const response = await api.post('/api/v1/telethon/backfill', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска бэкфилла'
        throw error
      }
    },

    async telethonBackfillSingleChat(chatId: number, limit: number = 5000) {
      try {
        const response = await api.post('/api/v1/telethon/backfill', { 
          limit: limit, 
          dialogs: chatId.toString() 
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки сообщений чата'
        throw error
      }
    },

    async telethonLoadForumTopics(chatId: number) {
      try {
        const response = await api.post(`/api/v1/telethon/load_forum_topics/${chatId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки тем форума'
        throw error
      }
    },

    async telethonBackfillTopic(chatId: number, topicId: number, limit: number = 5000) {
      try {
        const response = await api.post(`/api/v1/telethon/backfill_topic/${chatId}/${topicId}?limit=${limit}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки сообщений темы'
        throw error
      }
    },

    // Smart update and full reparse of messages for a single chat
    async smartUpdateChatMessages(chatId: number, withReactions: boolean = false, limit: number = 5000, telethonAccountId?: number | null) {
      try {
        const response = await api.post(`/api/v1/telethon/chat/${chatId}/smart_update_messages`, null, { 
          params: { 
            with_reactions: withReactions,
            limit: limit,
            ...(telethonAccountId ? { telethon_account_id: telethonAccountId } : {})
          } 
        })
        return response.data as { mode: 'incremental' | 'skipped', loaded?: number, reason?: string }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка smart-обновления сообщений'
        throw error
      }
    },

    async downloadThumbnail(messageId: number) {
      try {
        const response = await api.post(`/api/v1/telegram/messages/${messageId}/download_thumbnail`)
        return response.data as { url: string, status: 'downloaded' | 'cached' }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка скачивания thumbnail'
        throw error
      }
    },

    async downloadMessageMedia(messageId: number, expiresIn?: number) {
      try {
        const params = new URLSearchParams()
        if (expiresIn != null) {
          params.set('expires_in', String(expiresIn))
        }
        const response = await api.post(
          `/api/v1/telegram/messages/${messageId}/download_media${params.toString() ? `?${params.toString()}` : ''}`
        )
        return response.data as {
          url: string
          status: 'downloaded' | 'cached'
          media: {
            id: number
            file_name: string | null
            mime_type: string | null
            file_size: number | null
          }
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка скачивания файла'
        throw error
      }
    },

    async reparseAllChatMessages(chatId: number, withReactions: boolean = false, telethonAccountId?: number | null) {
      try {
        const response = await api.post(`/api/v1/telethon/chat/${chatId}/reparse_all_messages`, null, { 
          params: { 
            with_reactions: withReactions,
            ...(telethonAccountId ? { telethon_account_id: telethonAccountId } : {})
          } 
        })
        return response.data as { mode: 'full', loaded: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка полного перепарсинга сообщений'
        throw error
      }
    },

    async smartUpdateTopicMessages(chatId: number, topicId: number, limit: number = 5000, telethonAccountId?: number | null) {
      try {
        const response = await api.post(`/api/v1/telethon/chat/${chatId}/topic/${topicId}/smart_update_messages`, null, { 
          params: { 
            limit,
            ...(telethonAccountId ? { telethon_account_id: telethonAccountId } : {})
          } 
        })
        return response.data as { mode: 'incremental' | 'full', loaded: number, local_max_id_after: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка smart-обновления сообщений топика'
        throw error
      }
    },

    async telethonCheckForum(chatId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/check_forum/${chatId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка проверки форума'
        throw error
      }
    },

    async leaveAllChannels(username: string) {
      try {
        const response = await api.post(`/api/v1/telethon/accounts/${username}/leave_all_channels`)
        return response.data as {
          total: number
          left: number
          errors: number
          message: string
          results: Array<{
            chat_id: number
            telegram_id: number
            title: string
            status: 'success' | 'error'
            error?: string
          }>
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка массового выхода из каналов'
        throw error
      }
    },

  async telethonClearTopics(chatId: number) {
    try {
      const response = await api.delete(`/api/v1/telethon/clear_topics/${chatId}`)
      return response.data
    } catch (error: any) {
      this.error = error?.response?.data?.detail || 'Ошибка очистки тем'
      throw error
    }
  },

    async telethonBackfill10() {
      try {
        const response = await api.post('/api/v1/telethon/backfill', { limit: 10, dialogs: 'all' })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска тестового бэкфилла'
        throw error
      }
    },

    // Telethon QR login
    async telethonQrStart() {
      try {
        const response = await api.post('/api/v1/telethon/qr/start')
        return response.data as { qr: string }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска QR-логина'
        throw error
      }
    },
    async telethonQrStatus() {
      try {
        const response = await api.get('/api/v1/telethon/qr/status')
        return response.data as { session_ready: boolean, session_file?: string, password_required?: boolean }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка статуса QR-логина'
        throw error
      }
    },
    async telethonQrConfirmPassword(password: string) {
      try {
        const response = await api.post('/api/v1/telethon/qr/confirm_password', { password })
        return response.data as { session_ready: boolean, session_file?: string }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка подтверждения пароля'
        throw error
      }
    },

    async telethonSendMessage(chatId: number, text: string, replyToMsgId?: number, topicId?: number, internalChatId?: number) {
      try {
        const response = await api.post('/api/v1/telethon/send_message', {
          chat_id: chatId,
          text: text,
          reply_to_msg_id: replyToMsgId,
          topic_id: topicId,
          internal_chat_id: internalChatId
        })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка отправки сообщения'
        throw error
      }
    },

    async telethonSendMedia(
      chatId: number,
      mediaId: number,
      caption?: string,
      replyToMsgId?: number,
      topicId?: number,
      internalChatId?: number
    ) {
      try {
        const payload: any = {
          chat_id: chatId,
          media_id: mediaId,
        }
        if (caption) payload.caption = caption
        if (replyToMsgId != null) payload.reply_to_msg_id = replyToMsgId
        if (topicId != null) payload.topic_id = topicId
        if (internalChatId != null) payload.internal_chat_id = internalChatId

        const response = await api.post('/api/v1/telethon/send_media', payload)
        return response.data as { success: boolean; id: number; date: string }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка отправки файла'
        throw error
      }
    },

    async telethonMessagesRange(
      chatId: number, 
      limit: number = 50, 
      offsetId: number = 0,
      maxId: number = 0,
      minId: number = 0
    ) {
      try {
        const params = new URLSearchParams()
        params.set('limit', String(limit))
        if (offsetId > 0) params.set('offset_id', String(offsetId))
        if (maxId > 0) params.set('max_id', String(maxId))
        if (minId > 0) params.set('min_id', String(minId))
        
        const url = `/api/v1/telethon/chat/${chatId}/messages_range?${params.toString()}`
        console.log('[API] 🚀 POST', url)
        
        const response = await api.post(url)
        console.log('[API] ✅ Response:', response.status, response.data)
        
        return response.data as { loaded: number; has_more: boolean }
      } catch (error: any) {
        console.error('[API] ❌ Error:', error?.response?.status, error?.response?.data || error.message)
        this.error = error?.response?.data?.detail || 'Ошибка загрузки сообщений из Telegram'
        throw error
      }
    },

    async telethonGetChatHistory(chatId: number, limit: number = 50, offset: number = 0, topicId?: number) {
      try {
        const params = new URLSearchParams()
        params.set('limit', String(limit))
        params.set('offset', String(offset))
        if (topicId) params.set('topic_id', String(topicId))
        
        const response = await api.get(`/api/v1/telethon/chat/${chatId}/history?${params.toString()}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки истории'
        throw error
      }
    },

    async getTelegramMessages(chat_id?: number) {
      try {
        const url = chat_id 
          ? `/api/v1/telegram/messages?chat_id=${chat_id}`
          : '/api/v1/telegram/messages'
        const response = await api.get(url)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки сообщений'
        throw error
      }
    },

    async getTelegramUserMessages(telegramUserId: number, limit: number = 200, offset: number = 0) {
      try {
        const params = new URLSearchParams()
        params.set('limit', String(limit))
        params.set('offset', String(offset))
        const response = await api.get(`/api/v1/telegram/users/${telegramUserId}/messages?${params.toString()}`)
        return response.data as { items: any[]; total: number; limit: number; offset: number }
      } catch (error: any) {
        this.error = 'Ошибка загрузки сообщений пользователя'
        throw error
      }
    },

    async getTelegramChatRaw(chatOrTelegramId: number, telethonAccountId?: number | null) {
      try {
        const params = new URLSearchParams()
        if (telethonAccountId) params.append('telethon_account_id', telethonAccountId.toString())
        const url = `/api/v1/telegram/chats/${chatOrTelegramId}/raw${params.toString() ? '?' + params.toString() : ''}`
        const response = await api.get(url)
        return response.data as { id: number, telegram_id: number, raw: any }
      } catch (error: any) {
        this.error = 'Ошибка загрузки JSON чата'
        throw error
      }
    },

    // ============================================================================
    // АВАТАРКИ ЧАТОВ И ПОЛЬЗОВАТЕЛЕЙ TELEGRAM
    // ============================================================================

    /**
     * Скачать полную аватарку чата
     * @param chatId - Telegram ID чата
     * @param size - "big" (640x640) или "small" (160x160)
     * @param telethonAccountId - ID Telethon аккаунта (опционально)
     */
    async getChatPhoto(chatId: number, size: 'big' | 'small' = 'big', telethonAccountId?: number) {
      try {
        const params = new URLSearchParams({ size })
        if (telethonAccountId) {
          params.append('telethon_account_id', telethonAccountId.toString())
        }
        const response = await api.get(`/api/v1/telethon/chat/${chatId}/photo?${params}`)
        return response.data as { photo_base64: string, cached: boolean, size: string }
      } catch (error: any) {
        this.error = 'Ошибка загрузки фото чата'
        throw error
      }
    },

    /**
     * Получить превью аватарки чата (small photo 160x160px)
     * @param chatId - Telegram ID чата
     * @param telethonAccountId - ID Telethon аккаунта (опционально)
     */
    async getChatPhotoThumb(chatId: number, telethonAccountId?: number) {
      try {
        const params = new URLSearchParams()
        if (telethonAccountId) {
          params.append('telethon_account_id', telethonAccountId.toString())
        }
        // Используем быстрый эндпоинт /photo/thumb (читает из БД без API запросов)
        const url = `/api/v1/telegram/chats/${chatId}/photo/thumb${params.toString() ? '?' + params : ''}`
        const response = await api.get(url)
        return response.data as { photo_base64: string, source?: string, size: string }
      } catch (error: any) {
        // Не логируем ошибку - это нормально если нет превью
        throw error
      }
    },

    /**
     * Получить аватарки для нескольких чатов одним запросом (BATCH)
     * @param chatIds - Массив Telegram ID чатов
     * @param telethonAccountId - ID Telethon аккаунта (опционально)
     */
    async getChatPhotosBatch(chatIds: number[], telethonAccountId?: number) {
      try {
        const payload: any = { chat_ids: chatIds }
        if (telethonAccountId) {
          payload.telethon_account_id = telethonAccountId
        }
        const response = await api.post('/api/v1/telegram/chats/photos/batch', payload)
        return response.data as { photos: Record<number, string> }
      } catch (error: any) {
        console.error('[API] Batch photo load failed:', error)
        throw error
      }
    },

    /**
     * Скачать полную аватарку пользователя Telegram
     * @param userTelegramId - Telegram ID пользователя
     * @param size - "big" (640x640) или "small" (160x160)
     * @param telethonAccountId - ID Telethon аккаунта (опционально)
     */
    async getUserPhoto(userTelegramId: number, size: 'big' | 'small' = 'big', telethonAccountId?: number) {
      try {
        const params = new URLSearchParams({ size })
        if (telethonAccountId) {
          params.append('telethon_account_id', telethonAccountId.toString())
        }
        const response = await api.get(`/api/v1/telethon/user/${userTelegramId}/photo?${params}`)
        return response.data as { photo_base64: string, cached: boolean, size: string }
      } catch (error: any) {
        this.error = 'Ошибка загрузки фото пользователя'
        throw error
      }
    },

    /**
     * Получить список всех фото пользователя Telegram
     * @param userTelegramId - Telegram ID пользователя
     * @param limit - Максимум фото (до 100)
     * @param telethonAccountId - ID Telethon аккаунта (опционально)
     */
    async getUserPhotos(userTelegramId: number, limit: number = 20, telethonAccountId?: number) {
      try {
        const params = new URLSearchParams({ limit: limit.toString() })
        if (telethonAccountId) {
          params.append('telethon_account_id', telethonAccountId.toString())
        }
        const response = await api.get(`/api/v1/telethon/user/${userTelegramId}/photos?${params}`)
        return response.data as { 
          photos: Array<{
            id: string
            access_hash: string
            file_reference: string
            date: string | null
            is_current: boolean
            photo_base64: string
          }>, 
          total: number 
        }
      } catch (error: any) {
        this.error = 'Ошибка загрузки списка фото пользователя'
        throw error
      }
    },

    /**
     * Получить превью аватарки пользователя Telegram из БД (быстро, без API запросов)
     * @param userTelegramId - Telegram ID пользователя
     */
    async getUserPhotoThumb(userTelegramId: number, telethonAccountId?: number) {
      try {
        // 1) Сначала пробуем быстрый thumb из БД (без Telethon)
        try {
          const response = await api.get(`/api/v1/telegram/users/${userTelegramId}/photo/thumb`)
          return response.data as { photo_base64: string, source?: string, size: string }
        } catch (thumbErr: any) {
          // 404 = нет thumb в БД -> fallback на Telethon
          if (thumbErr?.response?.status !== 404) {
            throw thumbErr
          }
        }

        // 2) Fallback: Telethon (может быть медленно и/или без доступа)
        const params = new URLSearchParams({ size: 'small' })
        if (telethonAccountId) {
          params.append('telethon_account_id', telethonAccountId.toString())
        }
        const response = await api.get(`/api/v1/telethon/user/${userTelegramId}/photo?${params}`)
        return response.data as { photo_base64: string, cached?: boolean, size: string }
      } catch (error: any) {
        // Не логируем ошибку - это нормально если нет превью
        throw error
      }
    },

    /**
     * Получить превью фото из сообщения из БД (быстро, без API запросов)
     * @param messageId - Internal message ID (не telegram_id!)
     */
    async getMessagePhotoThumb(messageId: number) {
      try {
        const response = await api.get(`/api/v1/telegram/messages/${messageId}/photo/thumb`)
        return response.data as { photo_base64: string, source: string, size: string, message_id: number }
      } catch (error: any) {
        // Не логируем ошибку - это нормально если нет превью
        throw error
      }
    },

    // ============================================================================

    async markChatAsRead(chatId: number) {
      try {
        const response = await api.post(`/api/v1/telegram/chats/${chatId}/mark_read`)
        return response.data as { success: boolean, chat_id: number, last_read_at: string }
      } catch (error: any) {
        this.error = 'Ошибка отметки чата как прочитанного'
        throw error
      }
    },

    async pinChat(chatId: number, pin: boolean = true) {
      try {
        const response = await api.post(`/api/v1/telegram/chats/${chatId}/pin`, { pin })
        return response.data as { success: boolean, chat_id: number, pinned: boolean }
      } catch (error: any) {
        this.error = 'Ошибка закрепления чата'
        throw error
      }
    },

    async archiveChat(chatId: number, archive: boolean = true) {
      try {
        const response = await api.post(`/api/v1/telegram/chats/${chatId}/archive`, { archive })
        return response.data as { success: boolean, chat_id: number, archived: boolean }
      } catch (error: any) {
        this.error = 'Ошибка архивирования чата'
        throw error
      }
    },

    async muteChat(chatId: number, mute: boolean = true) {
      try {
        const response = await api.post(`/api/v1/telegram/chats/${chatId}/mute`, { mute })
        return response.data as { success: boolean, chat_id: number, muted: boolean }
      } catch (error: any) {
        this.error = 'Ошибка отключения уведомлений'
        throw error
      }
    },

    async addReaction(messageId: number, chatId: number, emoji: string) {
      try {
        const response = await api.post(
          `/api/v1/telethon/messages/${messageId}/reaction?chat_id=${chatId}&emoji=${encodeURIComponent(emoji)}`
        )
        return response.data as { success: boolean, message_id: number, emoji: string }
      } catch (error: any) {
        this.error = 'Ошибка добавления реакции'
        throw error
      }
    },

    async deleteMessage(messageId: number, chatId: number, revoke: boolean = true) {
      try {
        const response = await api.delete(
          `/api/v1/telethon/messages/${messageId}?chat_id=${chatId}&revoke=${revoke}`
        )
        return response.data as { success: boolean, message_id: number, deleted_count: number }
      } catch (error: any) {
        this.error = 'Ошибка удаления сообщения'
        throw error
      }
    },

    async forwardMessages(messageIds: number[], fromChatId: number, toChatId: number) {
      try {
        const response = await api.post('/api/v1/telethon/messages/forward', {
          message_ids: messageIds,
          from_chat_id: fromChatId,
          to_chat_id: toChatId
        })
        return response.data as { success: boolean, forwarded_count: number }
      } catch (error: any) {
        this.error = 'Ошибка пересылки сообщений'
        throw error
      }
    },

    async getTelegramMessageRaw(messageTelegramId: number) {
      try {
        const response = await api.get(`/api/v1/telegram/messages/${messageTelegramId}/raw`)
        return response.data as { id: number, telegram_id: number, raw: any }
      } catch (error: any) {
        this.error = 'Ошибка загрузки JSON сообщения'
        throw error
      }
    },

    async getTelethonHistoryRequest(chatId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/chat/${chatId}/history_request`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки GetHistoryRequest'
        throw error
      }
    },

    async getTelethonFullChannelRequest(chatId: number) {
      try {
        const response = await api.get(`/api/v1/telethon/chat/${chatId}/full_channel_request`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки GetFullChannelRequest'
        throw error
      }
    },

    async getTelethonChannelInfoByUsername(username: string) {
      try {
        const response = await api.get(`/api/v1/telethon/channel/${username}/info`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка загрузки информации о канале'
        throw error
      }
    },

    async addChatTag(chat_id: number, tag: string) {
      try {
        const response = await api.post(`/api/v1/telegram/chats/${chat_id}/tags`, { tag })
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка добавления тега'
        throw error
      }
    },

    async removeChatTag(chat_id: number, tag: string) {
      try {
        const response = await api.delete(`/api/v1/telegram/chats/${chat_id}/tags/${tag}`)
        return response.data
      } catch (error: any) {
        this.error = 'Ошибка удаления тега'
        throw error
      }
    },

    // Очистка ошибки
    clearError() {
      this.error = null
    },

    // Выход
    logout() {
      localStorage.removeItem('token')
    },

    // Telegram экспорт
    async exportTelegram(params: { format?: 'json'|'csv'|'txt'|'excel', chat_id?: number, topic_id?: number, limit?: number }) {
      const sp = new URLSearchParams()
      if (params.format) sp.set('format', params.format)
      if (params.chat_id != null) sp.set('chat_id', String(params.chat_id))
      if (params.topic_id != null) sp.set('topic_id', String(params.topic_id))
      if (params.limit != null) sp.set('limit', String(params.limit))
      const url = `/api/v1/telegram/export?${sp.toString()}`
      const token = localStorage.getItem('token')
      const response = await api.get(url, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      return response
    },

    async exportTelegramMulti(params: { chat_ids: number[], mode: 'mix'|'zip', format: 'json'|'csv'|'txt'|'excel' }) {
      const sp = new URLSearchParams()
      sp.set('chat_ids', params.chat_ids.join(','))
      sp.set('mode', params.mode)
      sp.set('format', params.format)
      const url = `/api/v1/telegram/export/multi?${sp.toString()}`
      const token = localStorage.getItem('token')
      const response = await api.get(url, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      return response
    },

    // ==================== Telegram Media (S3) ====================

    async uploadTelegramMedia(file: File, mediaType: string = 'document') {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('media_type', mediaType)

        const response = await api.post('/api/v1/telegram/media/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return response.data as {
          media_id: number
          file_hash: string
          file_size: number
          storage_key: string
          has_thumbnail: boolean
          is_duplicate: boolean
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки файла'
        throw error
      }
    },

    async getTelegramMediaUrl(mediaId: number, expiresIn?: number) {
      try {
        const params = new URLSearchParams()
        if (expiresIn != null) {
          params.set('expires_in', String(expiresIn))
        }
        const response = await api.get(`/api/v1/telegram/media/${mediaId}/url${params.toString() ? `?${params.toString()}` : ''}`)
        return response.data as {
          url: string
          expires_in: number
          media_info: {
            id: number
            media_type: string
            file_name: string | null
            mime_type: string | null
            file_size: number | null
            width: number | null
            height: number | null
            duration: number | null
            created_at: string | null
          }
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка получения ссылки на файл'
        throw error
      }
    },

    // ==========================================
    // CRM CONTACTS & TASKS API
    // ==========================================
    
    // Contacts
    async getContacts(params?: {
      scope?: string,
      telegram_group?: string,
      status?: string,
      stage?: string,
      source?: string,
      q?: string,
      location?: string,
      position?: string,
      department?: string,
      grade?: string,
      work_status?: string,
      contact_type?: string,
      company_name?: string,
      company_industry?: string,
      company_location?: string,
      company_employees_count?: string,
      company_site?: string,
      page?: number,
      page_size?: number,
      [key: string]: string | number | undefined | null,
    }) {
      const sp = new URLSearchParams()
      if (params?.status) sp.set('status', params.status)
      if (params?.stage) sp.set('stage', params.stage)
      if (params?.source) sp.set('source', params.source)
      if (params?.q) sp.set('q', params.q)
      if (params?.location) sp.set('location', params.location)
      if (params?.position) sp.set('position', params.position)
      if (params?.department) sp.set('department', params.department)
      if (params?.grade) sp.set('grade', params.grade)
      if (params?.scope) sp.set('scope', params.scope)
      if (params?.work_status) sp.set('work_status', params.work_status)
      if (params?.contact_type) sp.set('contact_type', params.contact_type)
      if (params?.telegram_group) sp.set('telegram_group', params.telegram_group)
      if (params?.company_name) sp.set('company_name', params.company_name)
      if (params?.company_industry) sp.set('company_industry', params.company_industry)
      if (params?.company_location) sp.set('company_location', params.company_location)
      if (params?.company_employees_count) sp.set('company_employees_count', params.company_employees_count)
      if (params?.company_site) sp.set('company_site', params.company_site)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      for (const [key, value] of Object.entries(params || {})) {
        if (!key.startsWith('company_catalog_')) continue
        if (value == null || value === '') continue
        sp.set(key, String(value))
      }
      const response = await api.get(`/api/v1/contacts?${sp.toString()}`)
      return response.data
    },

    async getContact(contactId: number) {
      const response = await api.get(`/api/v1/contacts/${contactId}`)
      return response.data
    },

    async getContactChats(contactId: number) {
      const response = await api.get(`/api/v1/contacts/${contactId}/chats`)
      return response.data as { chats: any[] }
    },

    async createContactChat(contactId: number, telethonAccountId?: number) {
      const response = await api.post(`/api/v1/contacts/${contactId}/chats/create`, {
        telethon_account_id: telethonAccountId ?? null,
      })
      return response.data as { created: boolean; chat_id?: number | null; chats: any[] }
    },

    async createContact(contact: {
      username?: string,
      phone?: string,
      first_name?: string,
      last_name?: string,
      company?: string,
      status?: string,
      stage?: string,
      source?: string,
      tags?: string[],
      priority?: number,
      notes?: string,
    }) {
      const response = await api.post('/api/v1/contacts', contact)
      return response.data
    },

    async updateContact(contactId: number, contact: {
      username?: string,
      phone?: string,
      first_name?: string,
      last_name?: string,
      company?: string,
      status?: string,
      stage?: string,
      source?: string,
      tags?: string[],
      priority?: number,
      notes?: string,
    }) {
      const response = await api.patch(`/api/v1/contacts/${contactId}`, contact)
      return response.data
    },

    async deleteContact(contactId: number) {
      const response = await api.delete(`/api/v1/contacts/${contactId}`)
      return response.data
    },

    async getContactTasks(contactId: number, status?: string) {
      const sp = new URLSearchParams()
      if (status) sp.set('status', status)
      const response = await api.get(`/api/v1/contacts/${contactId}/tasks?${sp.toString()}`)
      return response.data
    },

    async sendMessageToContact(contactId: number, message: string, taskId?: number) {
      const response = await api.post(`/api/v1/contacts/${contactId}/send-message`, {
        message,
        task_id: taskId
      })
      return response.data
    },

    // CRM SSOT cards
    async takePersonToWork(personUniqueId: number) {
      const response = await api.post(`/api/v1/crm/persons/${personUniqueId}/take-to-work`)
      return response.data
    },

    async getCrmPersonCard(personUniqueId: number, limitEvidence?: number) {
      const sp = new URLSearchParams()
      if (limitEvidence != null) sp.set('limit_evidence', String(limitEvidence))
      const response = await api.get(`/api/v1/crm/persons/${personUniqueId}${sp.toString() ? `?${sp.toString()}` : ''}`)
      return response.data
    },

    async getCrmCompanyCard(companyUniqueId: number, limitEvidence?: number) {
      const sp = new URLSearchParams()
      if (limitEvidence != null) sp.set('limit_evidence', String(limitEvidence))
      const response = await api.get(`/api/v1/crm/companies/${companyUniqueId}${sp.toString() ? `?${sp.toString()}` : ''}`)
      return response.data
    },

    // Кто на самом деле владеет компанией: раскрутка цепочки юрлиц вверх до людей.
    // Отдельной ручкой, а не в карточке: считается по запросу, за кнопкой.
    async getCrmCompanyUltimateOwners(companyUniqueId: number) {
      const response = await api.get(`/api/v1/crm/companies/${companyUniqueId}/ultimate-owners`)
      return response.data
    },

    // ── Рекламные ролики конкурентов ─────────────────────────────────────────
    // Данные мониторинга: какие ролики компании и отрасли крутятся на Rutube,
    // Zona и VK Video.
    async listAdCreatives(params?: {
      company_id?: number; industry?: string; source?: string; q?: string;
      product_class_id?: number; limit?: number; offset?: number
    }) {
      const sp = new URLSearchParams()
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
      })
      const r = await api.get(`/api/v1/crm/ad-creatives${sp.toString() ? `?${sp}` : ''}`)
      return r.data
    },

    async getAdCreativesSummary(params?: { company_id?: number; industry?: string }) {
      const sp = new URLSearchParams()
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
      })
      const r = await api.get(`/api/v1/crm/ad-creatives/summary${sp.toString() ? `?${sp}` : ''}`)
      return r.data
    },

    async getAdCreativesTimeline(params?: { company_id?: number; industry?: string; days?: number }) {
      const sp = new URLSearchParams()
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
      })
      const r = await api.get(`/api/v1/crm/ad-creatives/timeline${sp.toString() ? `?${sp}` : ''}`)
      return r.data
    },

    async getAdCreative(sha256: string) {
      const r = await api.get(`/api/v1/crm/ad-creatives/${sha256}`)
      return r.data
    },

    // ── Отрасли ──────────────────────────────────────────────────────────────
    async listIndustries() {
      const r = await api.get('/api/v1/crm/industries')
      return r.data
    },

    async getIndustryCard(industry: string, companiesLimit?: number) {
      const sp = new URLSearchParams()
      if (companiesLimit != null) sp.set('companies_limit', String(companiesLimit))
      const r = await api.get(
        `/api/v1/crm/industries/${encodeURIComponent(industry)}${sp.toString() ? `?${sp}` : ''}`)
      return r.data
    },

    async getIndustryNews(industry: string, limit?: number) {
      const sp = new URLSearchParams()
      if (limit != null) sp.set('limit', String(limit))
      const r = await api.get(
        `/api/v1/crm/industries/${encodeURIComponent(industry)}/news${sp.toString() ? `?${sp}` : ''}`)
      return r.data
    },

    async syncChatParticipants(chatTelegramId: number|string, opts?: { source_mode?: string, telethon_account_id?: number|null, limit?: number, include_full_user?: boolean }) {
      const sp = new URLSearchParams()
      sp.set('source_mode', opts?.source_mode || 'auto')
      if (opts?.telethon_account_id != null) sp.set('telethon_account_id', String(opts.telethon_account_id))
      if (opts?.limit != null) sp.set('limit', String(opts.limit))
      if (opts?.include_full_user != null) sp.set('include_full_user', String(opts.include_full_user))
      const r = await api.post(`/api/v1/telethon/chat/${chatTelegramId}/participants/sync?${sp.toString()}`)
      return r.data
    },

    async getCrmPersonConnections(personUniqueId: number, limit?: number) {
      const sp = new URLSearchParams(); if (limit!=null) sp.set('limit', String(limit))
      const r = await api.get(`/api/v1/crm/persons/${personUniqueId}/connections${sp.toString()?`?${sp}`:''}`)
      return r.data
    },

    async listCrmTelegramGroups() {
      const response = await api.get('/api/v1/crm/telegram-groups')
      return response.data
    },

    async getCrmPhotoCandidates(personUniqueId: number, opts?: { limit?: number; maxDistance?: number }) {
      const sp = new URLSearchParams()
      if (opts?.limit != null) sp.set('limit', String(opts.limit))
      if (opts?.maxDistance != null) sp.set('max_distance', String(opts.maxDistance))
      const r = await api.get(`/api/v1/crm/persons/${personUniqueId}/photo-candidates${sp.toString()?`?${sp}`:''}`)
      return r.data as { candidates: Array<{ photo_url: string; distance: number; current_owner?: string|null; attached: boolean }>; reference_faces?: number; note?: string }
    },

    async confirmCrmPhoto(personUniqueId: number, photoUrl: string, action: 'add'|'remove') {
      const r = await api.post(`/api/v1/crm/persons/${personUniqueId}/photo-confirm`, { photo_url: photoUrl, action })
      return r.data as { ok: boolean; event_photos: string[]; count: number }
    },

    async getCrmEvents() {
      const r = await api.get('/api/v1/crm/events')
      return r.data as { events: Array<{ key: string; title: string; photos: number; people: number; identified: number }> }
    },

    async getCrmEventPhotos(eventKey: string, opts?: { limit?: number; offset?: number; identifiedOnly?: boolean }) {
      const sp = new URLSearchParams()
      if (opts?.limit != null) sp.set('limit', String(opts.limit))
      if (opts?.offset != null) sp.set('offset', String(opts.offset))
      if (opts?.identifiedOnly) sp.set('identified_only', '1')
      const r = await api.get(`/api/v1/crm/events/${eventKey}/photos${sp.toString()?`?${sp}`:''}`)
      return r.data as { event: string; photos: Array<{ photo_url: string; unidentified: number; people: Array<{ id: number; name: string; avatar?: string|null; company?: string|null }> }> }
    },

    async getCrmFilterSuggestions(params: { field: string; q?: string; limit?: number }) {
      const sp = new URLSearchParams()
      sp.set('field', params.field)
      if (params.q) sp.set('q', params.q)
      if (params.limit) sp.set('limit', String(params.limit))
      const response = await api.get(`/api/v1/crm/filter-suggestions?${sp.toString()}`)
      return response.data as { items: Array<{ value: string; title: string; count: number }> }
    },

    async listCrmCompanies(params?: {
      q?: string
      industry?: string
      location?: string
      employees_count?: string
      site?: string
      ranking_dataset?: string
      clients_min?: number
      awards_min?: number
      ranking_year?: number
      lookalike_seed?: string
      sort_by?: string
      sort_order?: 'asc' | 'desc'
      page?: number
      page_size?: number
      [key: string]: string | number | undefined | null
    }) {
      const sp = new URLSearchParams()
      if (params?.q) sp.set('q', params.q)
      if (params?.industry) sp.set('industry', params.industry)
      if (params?.location) sp.set('location', params.location)
      if (params?.employees_count) sp.set('employees_count', params.employees_count)
      if (params?.site) sp.set('site', params.site)
      if (params?.ranking_dataset) sp.set('ranking_dataset', params.ranking_dataset)
      if (params?.clients_min) sp.set('clients_min', String(params.clients_min))
      if (params?.awards_min) sp.set('awards_min', String(params.awards_min))
      if (params?.ranking_year) sp.set('ranking_year', String(params.ranking_year))
      if (params?.lookalike_seed) sp.set('lookalike_seed', params.lookalike_seed)
      if (params?.sort_by) sp.set('sort_by', params.sort_by)
      if (params?.sort_order) sp.set('sort_order', params.sort_order)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      for (const [key, value] of Object.entries(params || {})) {
        if (!key.startsWith('catalog_')) continue
        if (value == null || value === '') continue
        sp.set(key, String(value))
      }
      const response = await api.get(`/api/v1/crm/companies?${sp.toString()}`)
      return response.data as {
        items: any[]
        total: number
        page: number
        page_size: number
        lookalike_seed?: string
        lookalike_title?: string
      }
    },

    async listCrmLookalikeSeeds() {
      const response = await api.get('/api/v1/crm/lookalike-seeds')
      return response.data as { items: Array<{ key: string; title: string; website: string; industry?: string; subindustry?: string; notes?: string }> }
    },

    async listCrmLegalEntities(params?: { q?: string; page?: number; page_size?: number }) {
      const sp = new URLSearchParams()
      if (params?.q) sp.set('q', params.q)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      const response = await api.get(`/api/v1/crm/legal-entities?${sp.toString()}`)
      return response.data as { items: any[]; total: number; page: number; page_size: number }
    },

    async listCrmSites(params?: {
      q?: string
      industry_ids?: number[] | null
      subindustry_ids?: number[] | null
      has_contacts?: number | null
      has_chats?: number | null
      page?: number
      page_size?: number
    }) {
      const sp = new URLSearchParams()
      if (params?.q) sp.set('q', params.q)
      if (Array.isArray(params?.industry_ids) && params!.industry_ids!.length) {
        sp.set('industry_ids', params!.industry_ids!.join(','))
      }
      if (Array.isArray(params?.subindustry_ids) && params!.subindustry_ids!.length) {
        sp.set('subindustry_ids', params!.subindustry_ids!.join(','))
      }
      if (params?.has_contacts != null) sp.set('has_contacts', String(params.has_contacts))
      if (params?.has_chats != null) sp.set('has_chats', String(params.has_chats))
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      const response = await api.get(`/api/v1/crm/sites?${sp.toString()}`)
      return response.data as { items: any[]; total: number; page: number; page_size: number }
    },

    async listCrmIndustries() {
      const response = await api.get('/api/v1/crm/industries')
      return response.data as { items: Array<{ id: number; name: string }> }
    },

    async listCrmSubindustries(params?: { industry_id?: number | null }) {
      const sp = new URLSearchParams()
      if (params?.industry_id != null) sp.set('industry_id', String(params.industry_id))
      const response = await api.get(`/api/v1/crm/subindustries${sp.toString() ? `?${sp.toString()}` : ''}`)
      return response.data as { items: Array<{ id: number; industry_id: number; name: string }> }
    },

    async listCrmCompanyPeople(companyUniqueId: number, limit: number = 200) {
      const sp = new URLSearchParams()
      if (limit != null) sp.set('limit', String(limit))
      const response = await api.get(`/api/v1/crm/companies/${companyUniqueId}/people?${sp.toString()}`)
      return response.data as { items: any[] }
    },

    async listCrmCompanyChats(companyUniqueId: number, params?: { limit?: number; llm_stage_nums?: number[] | null }) {
      const sp = new URLSearchParams()
      const lim = params?.limit ?? 200
      if (lim != null) sp.set('limit', String(lim))
      if (Array.isArray(params?.llm_stage_nums) && params!.llm_stage_nums!.length) {
        sp.set('llm_stage_nums', params!.llm_stage_nums!.join(','))
      }
      const response = await api.get(`/api/v1/crm/companies/${companyUniqueId}/chats?${sp.toString()}`)
      return response.data as { items: any[] }
    },

    async listCrmEvents(params?: { q?: string; page?: number; page_size?: number }) {
      const sp = new URLSearchParams()
      if (params?.q) sp.set('q', params.q)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      const response = await api.get(`/api/v1/crm/events?${sp.toString()}`)
      return response.data as { items: any[]; total: number; page: number; page_size: number }
    },

    async listCrmResearchSources(params?: {
      q?: string
      source_type?: string
      status?: string
      page?: number
      page_size?: number
    }) {
      const sp = new URLSearchParams()
      if (params?.q) sp.set('q', params.q)
      if (params?.source_type) sp.set('source_type', params.source_type)
      if (params?.status) sp.set('status', params.status)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      const response = await api.get(`/api/v1/crm/research-sources?${sp.toString()}`)
      return response.data as { items: any[]; total: number; page: number; page_size: number }
    },

    async listCompanyDomainReviews(params?: { limit?: number; offset?: number }) {
      const sp = new URLSearchParams()
      if (params?.limit != null) sp.set('limit', String(params.limit))
      if (params?.offset != null) sp.set('offset', String(params.offset))
      const response = await api.get(`/api/v1/company-enrichment/domain-reviews?${sp.toString()}`)
      return response.data as { items: any[]; total: number; limit: number; offset: number }
    },

    async approveCompanyDomainReview(companyUniqueId: number, domain: string) {
      const response = await api.post(`/api/v1/company-enrichment/domain-reviews/${companyUniqueId}/approve`, { domain })
      return response.data
    },

    async rejectCompanyDomainReview(companyUniqueId: number, payload?: { reason?: string; clear?: boolean }) {
      const response = await api.post(`/api/v1/company-enrichment/domain-reviews/${companyUniqueId}/reject`, payload || {})
      return response.data
    },

    async listCrmScores(params?: {
      entity_type?: 'person' | 'company' | 'event' | 'source'
      page?: number
      page_size?: number
    }) {
      const sp = new URLSearchParams()
      if (params?.entity_type) sp.set('entity_type', params.entity_type)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      const response = await api.get(`/api/v1/crm/scores${sp.toString() ? `?${sp.toString()}` : ''}`)
      return response.data as { items: any[]; total: number; page: number; page_size: number; score_version: string }
    },

    // CRM aggregation rules (editable matching rules)
    async listCrmAggregationRules() {
      const response = await api.get('/api/v1/crm/aggregation-rules')
      return response.data
    },

    async createCrmAggregationRule(payload: {
      entity_type: 'person' | 'company' | 'contact' | 'workexp',
      name: string,
      is_active?: boolean,
      priority?: number,
      rules_json?: any
    }) {
      const response = await api.post('/api/v1/crm/aggregation-rules', payload)
      return response.data
    },

    async updateCrmAggregationRule(ruleId: number, payload: {
      entity_type: 'person' | 'company' | 'contact' | 'workexp',
      name: string,
      is_active?: boolean,
      priority?: number,
      rules_json?: any
    }) {
      const response = await api.put(`/api/v1/crm/aggregation-rules/${ruleId}`, payload)
      return response.data
    },

    async runCrmAggregation(payload?: { entity_type?: 'person' | 'company' | 'contact' | 'workexp', dry_run?: boolean }) {
      const response = await api.post('/api/v1/crm/aggregation-rules/run', payload || {})
      return response.data
    },

    // Tasks
    async getTasks(params?: {
      status?: string,
      task_type?: string,
      contact_id?: number,
      execution_mode?: string,
      approval_status?: string,
      auto_send_enabled?: number,
      has_draft?: number,
      due_before?: string,
      include_no_due?: number,
      page?: number,
      page_size?: number
    }) {
      const sp = new URLSearchParams()
      if (params?.status) sp.set('status', params.status)
      if (params?.task_type) sp.set('task_type', params.task_type)
      if (params?.contact_id) sp.set('contact_id', String(params.contact_id))
      if (params?.execution_mode) sp.set('execution_mode', params.execution_mode)
      if (params?.approval_status) sp.set('approval_status', params.approval_status)
      if (params?.auto_send_enabled != null) sp.set('auto_send_enabled', String(params.auto_send_enabled))
      if (params?.has_draft != null) sp.set('has_draft', String(params.has_draft))
      if (params?.due_before) sp.set('due_before', params.due_before)
      if (params?.include_no_due != null) sp.set('include_no_due', String(params.include_no_due))
      if (params?.page) sp.set('page', String(params.page))
      if (params?.page_size) sp.set('page_size', String(params.page_size))
      const response = await api.get(`/api/v1/tasks?${sp.toString()}`)
      return response.data
    },

    async createTask(task: {
      contact_id: number,
      task_type: string,
      status?: string,
      source?: string,
      title?: string,
      description?: string,
      draft_message?: string,
      priority?: number,
      due_at?: string
    }) {
      const response = await api.post('/api/v1/tasks', task)
      return response.data
    },

    async updateTask(taskId: number, task: {
      status?: string,
      title?: string,
      description?: string,
      draft_message?: string,
      priority?: number,
      due_at?: string,
      error_message?: string,
      auto_send_enabled?: boolean
    }) {
      const response = await api.patch(`/api/v1/tasks/${taskId}`, task)
      return response.data
    },

    // ==================== Manager Settings ====================
    
    async getManagerSettings() {
      const response = await api.get('/api/v1/user/manager-settings')
      return response.data
    },

    async updateManagerSettings(settings: {
      max_meetings_per_day?: number,
      timezone?: string,
      work_schedule?: Record<string, any>
    }) {
      const response = await api.put('/api/v1/user/manager-settings', settings)
      return response.data
    },

    async connectGoogleCalendar() {
      const response = await api.get('/api/v1/user/connect-google-calendar')
      return response.data
    },

    async connectZoom() {
      const response = await api.get('/api/v1/user/connect-zoom')
      return response.data
    },

    // ==================== AI Bots API ====================
    
    async getAIBots() {
      try {
        const response = await api.get('/api/v1/ai-bots')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки AI ботов'
        throw error
      }
    },

    async getAIBotsDefaults() {
      try {
        const response = await api.get('/api/v1/ai-bots/defaults')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки дефолтных AI ботов'
        throw error
      }
    },

    async setDefaultCommunicationAIBot(botId: number) {
      try {
        const response = await api.post(`/api/v1/ai-bots/${botId}/set-default-communication`, {})
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка установки дефолтного бота для коммуникации'
        throw error
      }
    },

    async setDefaultAnalyticsAIBot(botId: number) {
      try {
        const response = await api.post(`/api/v1/ai-bots/${botId}/set-default-analytics`, {})
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка установки дефолтного бота для аналитики'
        throw error
      }
    },

    async ensureDefaultAIBots() {
      try {
        const response = await api.post('/api/v1/ai-bots/ensure-defaults', {})
        return response.data
      } catch (error: any) {
        // This is a best-effort helper; don't block UI flows if backend doesn't have endpoint yet.
        this.error = error?.response?.data?.detail || 'Ошибка ensure-defaults для AI ботов'
        throw error
      }
    },

    async getAnalyticsToolSchemaDefault() {
      try {
        const response = await api.get('/api/v1/analytics/tool-schema/default')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки дефолтной tool schema для аналитики'
        throw error
      }
    },

    async getAIBot(botId: number) {
      try {
        const response = await api.get(`/api/v1/ai-bots/${botId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки бота'
        throw error
      }
    },

    async createAIBot(bot: {
      name: string
      description?: string
      webhook_url: string
      api_key?: string
      timeout_seconds?: number
      default_context?: any
      telethon_account_id?: number | null
      proxy_id?: number | null
      ai_system_prompt_version_id?: number | null
      is_active?: boolean
    }) {
      try {
        const response = await api.post('/api/v1/ai-bots', bot)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания бота'
        throw error
      }
    },

    async updateAIBot(botId: number, bot: {
      name?: string
      description?: string
      webhook_url?: string
      api_key?: string
      timeout_seconds?: number
      default_context?: any
      telethon_account_id?: number | null
      proxy_id?: number | null
      ai_system_prompt_version_id?: number | null
      is_active?: boolean
    }) {
      try {
        const response = await api.patch(`/api/v1/ai-bots/${botId}`, bot)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления бота'
        throw error
      }
    },

    async deleteAIBot(botId: number) {
      try {
        const response = await api.delete(`/api/v1/ai-bots/${botId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления бота'
        throw error
      }
    },

    async getAIBotStats(botId: number) {
      try {
        const response = await api.get(`/api/v1/ai-bots/${botId}/stats`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки статистики'
        throw error
      }
    },

    async getAIBotLogs(botId: number, limit = 50, offset = 0) {
      try {
        const response = await api.get(`/api/v1/ai-bots/${botId}/logs?limit=${limit}&offset=${offset}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки логов'
        throw error
      }
    },

    async getAIBotToolsDefaults() {
      try {
        const response = await api.get('/api/v1/ai-bots/tools-defaults')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки tools по умолчанию'
        throw error
      }
    },

    async getAIBotsAnalyticsToolSchemas() {
      try {
        const response = await api.get('/api/v1/ai-bots/analytics-tool-schemas')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки analytics tool schemas'
        throw error
      }
    },

    async listAISystemPrompts() {
      try {
        const response = await api.get('/api/v1/ai-system-prompts')
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки версий system prompt'
        throw error
      }
    },

    async createAISystemPrompt(payload: {
      title?: string
      prompt_text: string
      set_as_tenant_default?: boolean
    }) {
      try {
        const response = await api.post('/api/v1/ai-system-prompts', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания версии system prompt'
        throw error
      }
    },

    async setActiveAISystemPrompt(versionId: number) {
      try {
        const response = await api.put('/api/v1/ai-system-prompts/active', { version_id: versionId })
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка установки активной версии system prompt'
        throw error
      }
    },

    // ==================== System Settings API (Admin) ====================
    
    async getSystemSettings() {
      const response = await api.get('/api/v1/admin/system-settings')
      return response.data
    },

    async updateSystemSettings(settings: {
      google_client_id?: string,
      google_client_secret?: string,
      zoom_client_id?: string,
      zoom_client_secret?: string,
      backend_url?: string,
      timezone?: string,
      crm_contact_sync_mode?: 'all_private' | 'tagged_only' | 'disabled',
      crm_contact_sync_tag_filter?: string[] | string
    }) {
      const response = await api.put('/api/v1/admin/system-settings', settings)
      return response.data
    },

    // ==================== Admin: Users Management ====================
    
    async getUsers() {
      const response = await api.get('/api/v1/users')
      return response.data
    },
    
    async setUserManagerRole(userId: number, roleSettings: {
      is_sales_manager: boolean,
      max_meetings_per_day?: number,
      timezone?: string,
      work_schedule?: { [key: string]: { start: string, end: string } | null }
    }) {
      const response = await api.post(`/api/v1/admin/users/${userId}/set-manager-role`, roleSettings)
      return response.data
    },

    async adminCreateUser(data: {
      email: string
      username: string
      password: string
      role: string
      tenant_id?: number
    }) {
      const response = await api.post('/api/v1/admin/users', data)
      return response.data
    },

    // ==================== Admin: Proxy Management ====================
    
    async getProxyPool(params?: {
      country?: string,
      is_active?: boolean,
      is_assigned?: boolean,
      purpose?: 'telethon' | 'ai'
    }) {
      const response = await api.get('/api/v1/admin/proxy-pool', { params })
      return response.data
    },

    async createProxy(proxy: {
      name: string,
      type: string,
      host: string,
      port: number,
      purpose?: 'telethon' | 'ai',
      username?: string,
      password?: string,
      country?: string,
      city?: string,
      provider?: string,
      is_active?: boolean,
      is_sticky?: boolean,
      rotation_minutes?: number,
      bandwidth_limit_gb?: number,
      active_until?: string
    }) {
      const response = await api.post('/api/v1/admin/proxy-pool', proxy)
      return response.data
    },

    async updateProxy(proxyId: number, proxy: any) {
      const response = await api.put(`/api/v1/admin/proxy-pool/${proxyId}`, proxy)
      return response.data
    },

    async deleteProxy(proxyId: number) {
      const response = await api.delete(`/api/v1/admin/proxy-pool/${proxyId}`)
      return response.data
    },

    async importProxiesFromProvider(importRequest: {
      api_key: string,
      country?: string | null,
      auto_assign?: boolean
    }) {
      const response = await api.post('/api/v1/admin/proxy-pool/import', importRequest)
      return response.data
    },

    async assignProxyToAccount(assignRequest: {
      account_id: number,
      proxy_id?: number,
      country?: string,
      prefer_sticky?: boolean
    }) {
      const response = await api.post('/api/v1/admin/proxy-pool/assign', assignRequest)
      return response.data
    },

    async unassignProxyFromAccount(accountId: number) {
      const response = await api.post('/api/v1/admin/proxy-pool/unassign', {
        account_id: accountId
      })
      return response.data
    },

    async getProxyStats() {
      const response = await api.get('/api/v1/admin/proxy-stats')
      return response.data
    },

    async getTelethonAccountsWithProxy() {
      const response = await api.get('/api/v1/admin/telethon-accounts-proxy')
      return response.data
    },

    async updateTelethonAccountProxySettings(accountId: number, settings: {
      proxy_enabled?: boolean,
      proxy_auto_rotate?: boolean
    }) {
      const response = await api.put(`/api/v1/admin/telethon-accounts/${accountId}/proxy-settings`, settings)
      return response.data
    },

    async testTelethonAccountProxy(accountId: number) {
      const response = await api.get(`/api/v1/admin/telethon-accounts/${accountId}/test-proxy`)
      return response.data
    },

    async getTelethonEventsHourly(params: {
      since?: string
      until?: string
      tenant_id?: number | null
      telethon_account_id?: number | null
      event_type?: string
    } = {}) {
      const response = await api.get('/api/v1/admin/telethon-events/hourly', { params })
      return response.data
    },

    async getTelethonEventsRecent(params: {
      limit?: number
      tenant_id?: number | null
      telethon_account_id?: number | null
      event_type?: string | null
    } = {}) {
      const response = await api.get('/api/v1/admin/telethon-events/recent', { params })
      return response.data
    },

    // ==================== Admin: Tenants Management (Multi-Tenancy) ====================
    
    async getTenants(includeInactive: boolean = false) {
      const params = includeInactive ? '?include_inactive=true' : ''
      const response = await api.get(`/api/v1/admin/tenants${params}`)
      return response.data
    },

    async getTenant(tenantId: number) {
      const response = await api.get(`/api/v1/admin/tenants/${tenantId}`)
      return response.data
    },

    async createTenant(tenantData: {
      name: string
      slug: string
      is_active?: boolean
      subscription_plan?: string
      max_users?: number
      max_chats?: number
      max_messages_per_month?: number
      notes?: string
    }) {
      const response = await api.post('/api/v1/admin/tenants', tenantData)
      return response.data
    },

    async updateTenant(tenantId: number, tenantData: {
      name?: string
      slug?: string
      is_active?: boolean
      subscription_plan?: string
      max_users?: number
      max_chats?: number
      max_messages_per_month?: number
      notes?: string
      settings?: any
    }) {
      const response = await api.put(`/api/v1/admin/tenants/${tenantId}`, tenantData)
      return response.data
    },

    async deleteTenant(tenantId: number, force: boolean = false) {
      const params = force ? '?force=true' : ''
      const response = await api.delete(`/api/v1/admin/tenants/${tenantId}${params}`)
      return response.data
    },

    async getTenantSettings(tenantId: number) {
      const response = await api.get(`/api/v1/admin/tenants/${tenantId}/settings`)
      return response.data
    },

    async updateTenantSettings(tenantId: number, settings: {
      google_client_id?: string
      google_client_secret?: string
      zoom_client_id?: string
      zoom_client_secret?: string
      openai_api_key?: string
      openai_model?: string
      yandex_s3_key_id?: string
      yandex_s3_key_secret?: string
      yandex_s3_endpoint?: string
      backend_url?: string
      frontend_url?: string
      timezone?: string
      ai_bot_enabled?: boolean
      ai_bot_temperature?: number
    }) {
      const response = await api.put(`/api/v1/admin/tenants/${tenantId}/settings`, settings)
      return response.data
    },

    // ==================== INVITATIONS ====================

    /**
     * Создать инвайт
     */
    async createInvitation(data: {
      email: string
      tenant_id: number
      role: string
      expires_in_days?: number
      notes?: string
      send_email?: boolean
    }) {
      const { send_email = true, ...invitationData } = data
      const response = await api.post(
        `/api/v1/invitations?send_email=${send_email}`,
        invitationData
      )
      return response.data
    },

    /**
     * Получить список инвайтов
     */
    async getInvitations(filters?: {
      status?: string
      page?: number
      page_size?: number
    }) {
      const params = new URLSearchParams()
      if (filters?.status) params.append('status', filters.status)
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.page_size) params.append('page_size', filters.page_size.toString())
      
      const response = await api.get(`/api/v1/invitations?${params}`)
      return response.data
    },

    /**
     * Проверить валидность инвайта (без авторизации)
     */
    async validateInvitation(token: string) {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/invitations/validate/${token}`
      )
      return response.data
    },

    /**
     * Отозвать инвайт
     */
    async revokeInvitation(invitationId: number) {
      await api.delete(`/api/v1/invitations/${invitationId}`)
    },

    /**
     * Отправить тестовое письмо
     */
    async sendTestEmail(email: string) {
      const response = await api.post(`/api/v1/invitations/test-email?email=${email}`)
      return response.data
    },

    // ==================== USER TENANTS ====================

    /**
     * Получить список тенантов пользователя
     */
    async getUserTenants() {
      const response = await api.get('/api/v1/user/tenants')
      return response.data
    },

    /**
     * Переключиться на другой тенант
     */
    async switchTenant(tenantId: number) {
      const response = await api.post(`/api/v1/user/switch-tenant/${tenantId}`)
      return response.data
    },

    /**
     * Создать свой тенант
     */
    async createOwnTenant(data: {
      name: string
      slug: string
      notes?: string
    }) {
      const response = await api.post('/api/v1/tenants/create-own', data)
      return response.data
    },

    // ==================== Analytics (MVP) ====================

    async analyticsCreateReport(payload: { name: string, report_type: string, config: any }) {
      try {
        const response = await api.post('/api/v1/analytics/reports', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания отчета'
        throw error
      }
    },

    async analyticsListReports() {
      try {
        const response = await api.get('/api/v1/analytics/reports')
        return response.data as any[]
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки отчетов'
        throw error
      }
    },

    async analyticsUpdateReport(reportId: number, payload: { name?: string | null, config?: any }) {
      try {
        const response = await api.patch(`/api/v1/analytics/reports/${reportId}`, payload || {})
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления отчета'
        throw error
      }
    },

    async analyticsRunReport(reportId: number, payload?: {
      max_chats?: number | null
      // Daily report params (optional)
      day?: string | null
      tz?: string | null
      chat_filter_mode?: string | null
      transcript_mode?: string | null
      last_n?: number | null
      day_context_before?: number | null
      day_context_after?: number | null
      create_tasks?: boolean | null
      // allow forward-compatible extra fields
      [k: string]: any
    }) {
      try {
        const response = await api.post(`/api/v1/analytics/reports/${reportId}/run`, payload || {})
        return response.data as { run_id: number, report_id: number, status: string }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска отчета'
        throw error
      }
    },

    async analyticsStopRun(runId: number) {
      try {
        const response = await api.post(`/api/v1/analytics/runs/${runId}/stop`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка остановки запуска'
        throw error
      }
    },

    async analyticsFailRun(runId: number, reason?: string) {
      try {
        const response = await api.post(`/api/v1/analytics/runs/${runId}/fail`, reason ? { reason } : {})
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка перевода запуска в failed'
        throw error
      }
    },

    async analyticsGetActiveRuns() {
      try {
        const response = await api.get(`/api/v1/analytics/runs/active`)
        return response.data as any[]
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки активных запусков'
        throw error
      }
    },

    async analyticsListDailyRuns(limit: number = 200) {
      try {
        const response = await api.get(`/api/v1/analytics/daily-runs`, { params: { limit } })
        return response.data as any[]
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки daily runs'
        throw error
      }
    },

    async analyticsGetRunSankey(runId: number) {
      try {
        const response = await api.get(`/api/v1/analytics/runs/${runId}/sankey`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки воронки'
        throw error
      }
    },

    async analyticsListReportRuns(reportId: number) {
      try {
        const response = await api.get(`/api/v1/analytics/reports/${reportId}/runs`)
        return response.data as any[]
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки запусков отчета'
        throw error
      }
    },

    async analyticsGetRun(runId: number) {
      try {
        const response = await api.get(`/api/v1/analytics/runs/${runId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки запуска'
        throw error
      }
    },

    async analyticsListRunItems(runId: number, page: number = 1, page_size: number = 50) {
      try {
        const response = await api.get(`/api/v1/analytics/runs/${runId}/items`, { params: { page, page_size } })
        return response.data as { total: number, page: number, page_size: number, items: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки результатов отчета'
        throw error
      }
    },

    async analyticsCreateRunSummary(runId: number) {
      try {
        // Summary can take 1-3+ minutes depending on dataset size and provider latency.
        // Increase timeout and disable retry to avoid spawning duplicates / confusing UI.
        const response = await api.post(
          `/api/v1/analytics/runs/${runId}/summary`,
          {},
          { timeout: 180000, __noRetry: true } as any
        )
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка генерации summary'
        throw error
      }
    },

    async analyticsGetChatsLlmLabels(
      payload: {
      chat_ids: number[]
      telethon_account_id?: number | null
      telethon_account_tag?: string | null
      },
      opts?: { signal?: AbortSignal }
    ) {
      try {
        const response = await api.post('/api/v1/analytics/chats/llm_labels', payload, { signal: opts?.signal } as any)
        return response.data as {
          items: Array<{
            chat_row_id: number
            report_id: number | null
            run_id: number | null
            last_analyzed_at: string | null
            confidence: number | null
            created_at: string | null
            analysis: any | null
          }>
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки LLM-разметки диалогов'
        throw error
      }
    },

    async analyticsGetChatLlmRaw(payload: {
      chat_row_id: number
      telethon_account_id?: number | null
      telethon_account_tag?: string | null
    }) {
      try {
        const response = await api.post('/api/v1/analytics/chats/llm_raw', payload)
        return response.data as {
          chat_row_id: number
          report_id: number | null
          run_id: number | null
          last_analyzed_at: string | null
          confidence: number | null
          created_at: string | null
          analysis: any | null
          llm_raw: any | null
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки RAW LLM-лога'
        throw error
      }
    },

    async analyticsReanalyzeChat(chat_row_id: number, payload: {
      provider?: string | null
      model?: string | null
      temperature?: number | null
      max_tokens?: number | null
      prompt_override?: string | null
      transcript_mode?: string | null
      last_n?: number | null
    }) {
      try {
        // Reanalyze может занимать 1-2+ минуты (LLM + коррекция JSON),
        // поэтому даём увеличенный timeout и выключаем retry, чтобы не плодить дублирующие прогоны.
        const response = await api.post(
          `/api/v1/analytics/chats/${chat_row_id}/reanalyze`,
          payload || {},
          { timeout: 180000, __noRetry: true } as any
        )
        return response.data as {
          chat_row_id: number
          report_id: number
          run_id: number
          analysis: any
          llm_raw: any
          confidence: number
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка переанализа чата'
        throw error
      }
    },

    async llmListModels(provider?: string, q?: string, limit?: number) {
      try {
        const params: any = {}
        if (provider) params.provider = provider
        if (q) params.q = q
        if (limit) params.limit = limit
        const response = await api.get('/api/v1/llm/models', { params })
        return response.data as { default_model: string, models: string[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки списка моделей'
        throw error
      }
    },

    // ==================== Analytics Auto Jobs ====================
    
    async getAvailableReportsForAutoJobs() {
      try {
        const response = await api.get('/api/v1/analytics/auto-jobs/reports')
        return response.data as { reports: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки reports'
        throw error
      }
    },

    async getAnalyticsAutoJobs() {
      try {
        const response = await api.get('/api/v1/analytics/auto-jobs')
        return response.data as { jobs: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки auto jobs'
        throw error
      }
    },

    async createAnalyticsAutoJob(payload: any) {
      try {
        const response = await api.post('/api/v1/analytics/auto-jobs', payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания auto job'
        throw error
      }
    },

    async updateAnalyticsAutoJob(jobId: number, payload: any) {
      try {
        const response = await api.patch(`/api/v1/analytics/auto-jobs/${jobId}`, payload)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления auto job'
        throw error
      }
    },

    async deleteAnalyticsAutoJob(jobId: number) {
      try {
        const response = await api.delete(`/api/v1/analytics/auto-jobs/${jobId}`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления auto job'
        throw error
      }
    },

    async getAnalyticsAutoJobStats(jobId: number) {
      try {
        const response = await api.get(`/api/v1/analytics/auto-jobs/${jobId}/stats`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки статистики'
        throw error
      }
    },

    async recoverAnalyticsAutoJob(jobId: number) {
      try {
        const response = await api.post(`/api/v1/analytics/auto-jobs/${jobId}/recover`)
        return response.data
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска recovery'
        throw error
      }
    },

    // ==================== CRM Outreach (Mass Mailing Planner) ====================

    async getCrmOutreachJobs() {
      try {
        const response = await api.get('/api/v1/crm/outreach/jobs')
        return response.data as { items: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки outreach jobs'
        throw error
      }
    },

    async createCrmOutreachJob(payload: any) {
      try {
        const response = await api.post('/api/v1/crm/outreach/jobs', payload)
        return response.data as { success: boolean, job_id: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка создания outreach job'
        throw error
      }
    },

    async updateCrmOutreachJob(jobId: number, payload: any) {
      try {
        const response = await api.put(`/api/v1/crm/outreach/jobs/${jobId}`, payload)
        return response.data as { success: boolean, job_id: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка обновления outreach job'
        throw error
      }
    },

    async deleteCrmOutreachJob(jobId: number) {
      try {
        const response = await api.delete(`/api/v1/crm/outreach/jobs/${jobId}`)
        return response.data as { success: boolean }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка удаления outreach job'
        throw error
      }
    },

    async previewCrmOutreachJob(jobId: number) {
      try {
        const response = await api.post(`/api/v1/crm/outreach/jobs/${jobId}/preview`)
        return response.data as any
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка preview outreach job'
        throw error
      }
    },

    async triggerCrmOutreachJob(jobId: number, targetDate?: string | null) {
      try {
        const response = await api.post(`/api/v1/crm/outreach/jobs/${jobId}/trigger`, {
          target_date: targetDate || null,
        })
        return response.data as { success: boolean, run_id: number, run_key: string }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска outreach job'
        throw error
      }
    },

    async getCrmOutreachJobRuns(jobId: number) {
      try {
        const response = await api.get(`/api/v1/crm/outreach/jobs/${jobId}/runs`)
        return response.data as { items: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки outreach runs'
        throw error
      }
    },

    async getCrmOutreachRun(runId: number) {
      try {
        const response = await api.get(`/api/v1/crm/outreach/runs/${runId}`)
        return response.data as any
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки outreach run'
        throw error
      }
    },

    async getCrmOutreachRunItems(runId: number, page: number = 1, pageSize: number = 50) {
      try {
        const response = await api.get(`/api/v1/crm/outreach/runs/${runId}/items`, {
          params: { page, page_size: pageSize },
        })
        return response.data as { items: any[], page: number, page_size: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки outreach run items'
        throw error
      }
    },

    // ==================== Tasks (AI-Prompter & Auto-Reply) ====================
    
    async getPendingDrafts(limit: number = 50) {
      try {
        const response = await api.get('/api/v1/tasks/pending-drafts', { params: { limit } })
        return response.data as { items: any[], total: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки черновиков'
        throw error
      }
    },

    async getChatPendingDrafts(chatId: number) {
      try {
        const response = await api.get(`/api/v1/tasks/chat/${chatId}/pending-drafts`)
        return response.data as { items: any[], total: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки черновиков чата'
        throw error
      }
    },

    async getCrmChatEvents(chatId: number, limit: number = 30) {
      try {
        const response = await api.get(`/api/v1/crm/chats/${Number(chatId)}/events`, {
          params: { limit: Number(limit || 30) },
        })
        return response.data as { items: any[], total: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки событий базы знаний'
        throw error
      }
    },

    // Messenger: lightweight "flow runs" (Elena analytics pipeline)
    async getAnalyticsChatFlowRuns(chatId: number, limit: number = 30) {
      try {
        const response = await api.get(`/api/v1/analytics/chats/${Number(chatId)}/flow-runs`, {
          params: { limit: Number(limit || 30) },
        })
        return response.data as { chat_row_id: number, items: any[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки запусков флоу'
        throw error
      }
    },

    async getAnalyticsItem(itemId: number) {
      try {
        const response = await api.get(`/api/v1/analytics/items/${Number(itemId)}`)
        return response.data as any
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка загрузки аналитики'
        throw error
      }
    },

    async approveTask(taskId: number, editedMessage?: string) {
      try {
        const response = await api.post(`/api/v1/tasks/${taskId}/approve`, {
          edited_message: editedMessage || null
        })
        return response.data as { success: boolean, task_id: number, message_id: number, is_edited: boolean }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка отправки черновика'
        throw error
      }
    },

    async rejectTask(taskId: number, reason?: string) {
      try {
        const response = await api.post(`/api/v1/tasks/${taskId}/reject`, {
          reason: reason || null
        })
        return response.data as { success: boolean, task_id: number }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка отклонения черновика'
        throw error
      }
    },

    async regenerateTask(taskId: number, model?: string) {
      try {
        const response = await api.post(`/api/v1/tasks/${taskId}/regenerate`, {
          model: model || null
        })
        return response.data as { 
          success: boolean, 
          old_task_id: number, 
          new_task_id: number, 
          new_draft: string,
          best_answer_time_at?: string,
          due_at?: string,
        }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка регенерации черновика'
        throw error
      }
    },

    async switchChatToAutoMode(chatId: number, enabled: boolean, allowedActions?: string[]) {
      try {
        const response = await api.post(`/api/v1/tasks/chat/${chatId}/switch-to-auto`, {
          enabled: enabled,
          allowed_actions: allowedActions || null
        })
        return response.data as { success: boolean, chat_id: number, auto_mode_enabled: boolean, allowed_actions: string[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка переключения режима автоответов'
        throw error
      }
    },

    async getAutoModeStatus(chatId: number) {
      try {
        const response = await api.get(`/api/v1/tasks/chat/${chatId}/auto-mode-status`)
        return response.data as { chat_id: number, auto_mode_enabled: boolean, allowed_actions: string[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка получения статуса автоответов'
        throw error
      }
    },

    async backfillDrafts(payload: {
      telethon_account_id?: number | null
      telethon_account_tag?: string | null
      only_private?: boolean
      only_active?: boolean
      only_without_pending_draft?: boolean
      limit?: number
    }) {
      try {
        const response = await api.post('/api/v1/tasks/backfill-drafts', payload || {})
        return response.data as { started: boolean, queued: number, processed?: number, chat_ids: number[] }
      } catch (error: any) {
        this.error = error?.response?.data?.detail || 'Ошибка запуска догонялки черновиков'
        throw error
      }
    },

    // ---- Wiki / FAQ (hierarchical knowledge base) ----
    async getWikiTree(space: string = 'finance') {
      const response = await api.get('/api/v1/wiki/tree', { params: { space } })
      return response.data as {
        space: string
        nodes: Array<{
          id: number
          parent_id: number | null
          space: string
          slug: string
          title: string
          is_category: boolean
          sort_order: number
          tags: string[]
          updated_at: string | null
        }>
      }
    },
    async getWikiArticle(id: number) {
      const response = await api.get(`/api/v1/wiki/articles/${Number(id)}`)
      return response.data
    },
    async searchWiki(opts: { q?: string; tag?: string; space?: string; limit?: number }) {
      const response = await api.get('/api/v1/wiki/search', {
        params: {
          q: opts.q || '',
          tag: opts.tag || undefined,
          space: opts.space || 'finance',
          limit: opts.limit || 50,
        },
      })
      return response.data as {
        query: string
        tag: string | null
        results: Array<{
          id: number
          parent_id: number | null
          slug: string
          title: string
          is_category: boolean
          tags: string[]
          snippet: string
        }>
      }
    },
    async getWikiTags(space: string = 'finance') {
      const response = await api.get('/api/v1/wiki/tags', { params: { space } })
      return response.data as { tags: Array<{ tag: string; count: number }> }
    },

    // ---- Wiki pages (пользовательские HTML-страницы с шарингом по тенанту) ----
    async getWikiPages(q: string = '') {
      const response = await api.get('/api/v1/wiki-pages', { params: q ? { q } : {} })
      return response.data as {
        pages: Array<{
          id: number
          slug: string
          title: string
          visibility: 'private' | 'tenant' | 'people'
          author_id: number | null
          author_name: string | null
          updated_at: string | null
          mine: boolean
        }>
      }
    },
    async getWikiPage(id: number) {
      const response = await api.get(`/api/v1/wiki-pages/${Number(id)}`)
      return response.data as {
        id: number
        slug: string
        title: string
        body_html: string
        visibility: 'private' | 'tenant' | 'people'
        author_id: number | null
        author_name: string | null
        created_at: string | null
        updated_at: string | null
        can_edit: boolean
        shared_user_ids: number[]
        url: string
      }
    },
    async getWikiPageMembers() {
      const response = await api.get('/api/v1/wiki-pages/members')
      return response.data as { members: Array<{ id: number; username: string; full_name: string }> }
    },
    async createWikiPage(body: {
      title: string
      body_html: string
      visibility: string
      shared_user_ids?: number[]
    }) {
      const response = await api.post('/api/v1/wiki-pages', body)
      return response.data as { id: number; slug: string; url: string }
    },
    async updateWikiPage(id: number, body: Record<string, any>) {
      const response = await api.put(`/api/v1/wiki-pages/${Number(id)}`, body)
      return response.data
    },
    async deleteWikiPage(id: number) {
      const response = await api.delete(`/api/v1/wiki-pages/${Number(id)}`)
      return response.data
    },

    // Универсальный таск-борд: те же ручки, что у агентов (см. agent_task_board_router).
    async listBoardTasks(params: Record<string, any> = {}) {
      const response = await api.get('/api/v1/task-board/tasks', { params })
      return response.data as { total: number; items: any[] }
    },
    async getBoardSummary() {
      const response = await api.get('/api/v1/task-board/summary')
      return response.data
    },
    async createBoardTask(body: Record<string, any>) {
      const response = await api.post('/api/v1/task-board/tasks', body)
      return response.data
    },
    async patchBoardTask(taskId: number, body: Record<string, any>) {
      const response = await api.patch(`/api/v1/task-board/tasks/${Number(taskId)}`, body)
      return response.data
    },
    async cancelBoardTask(taskId: number) {
      const response = await api.delete(`/api/v1/task-board/tasks/${Number(taskId)}`)
      return response.data
    },
    async attachBoardThread(taskId: number, body: Record<string, any>) {
      const response = await api.post(`/api/v1/task-board/tasks/${Number(taskId)}/threads`, body)
      return response.data
    },
  }
})
